// src/components/TransportSessionLocations.js

import React, {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";

import Sortable from "sortablejs";
import axios from "../axiosConfig";

import {
  FaGripLines,
  FaMapMarkerAlt,
  FaBullseye,
  FaTrash,
  FaEdit,
  FaCamera,
  FaGasPump,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaCommentDots,
} from "react-icons/fa";

import { Truck, User, Container } from "lucide-react";
import "../styles/TransportSessionLocations.css";

import DateConflictModal from "./DateConflictModal";
import TruckSelectorModal from "./TruckSelectorModal";
import TrailerSelectorModal from "./TrailerSelectorModal";
import DriverSelectorModal from "./DriverSelectorModal";
import FreeTextTruckModal from "./FreeTextTruckModal";
import FreeTextDriverModal from "./FreeTextDriverModal";
import StrategicPointModal from "./StrategicPointModal";
import StatusSelectorModal from "./StatusSelectorModal";
import CMRLoadModal from "./CMRLoadModal";
import TruckInventoryModal from "./TruckInventoryModal";
import TrailerInventoryModal from "./TrailerInventoryModal";
import EditStrategicPointModal from "./EditStrategicPointModal";
import TransportSessionMapModal from "./TransportSessionMapModal";
import ModalPortal from "./ModalPortal";
import { getSocket } from "../lib/socket";
import { useTranslation } from "react-i18next";

/* ------------------------- Carrier helper ------------------------- */
function getMyCompanyIdSafe() {
  try {
    const direct =
      localStorage.getItem("companyId") ||
      localStorage.getItem("company_id") ||
      null;
    if (direct) return direct;

    const companyRaw = localStorage.getItem("company");
    if (companyRaw) {
      const obj = JSON.parse(companyRaw);
      return obj?.id ?? obj?.companyId ?? obj?.company_id ?? null;
    }
  } catch {}
  return null;
}

function isExternalTransportCarrier(load) {
  const myCompanyId = getMyCompanyIdSafe();
  const myId = myCompanyId != null ? String(myCompanyId) : null;

  // Se não sei quem sou, não arrisco "external"
  if (!myId) return false;

  // ✅ Campo que tu já usas no Loads.js e está no summary-with-progress
  const carrierId =
    load?.carrierId ??
    load?.carrier_id ??
    load?.carrier?.id ??
    load?.transportCarrierId ??           // fallback ok
    load?.transport_carrier_id ??
    load?.transportCarrier?.id ??
    null;

  // Se não vem carrierId, não assumes external aqui (senão dá falsos positivos)
  if (carrierId == null) return false;

  return String(carrierId) !== myId;
}

// 🔎 Debug helper (enable with: localStorage.setItem("debugTruckRouting","1"))
// 🔎 Debug helper (enable with: localStorage.setItem("debugTruckRouting","1"))
const dbgTruck = (...args) => {
  try {
    if (localStorage.getItem("debugTruckRouting") === "1") {
      console.log(...args);
    }
  } catch {}
};

// 🔎 Driver routing debug (enable with: localStorage.setItem("debugDriverRouting","1"))
const dbgDriver = (...args) => {
  try {
    if (localStorage.getItem("debugDriverRouting") === "1") {
      console.log(...args);
    }
  } catch {}
};

// --- Derive display values from "manual" load info fields (external carriers) ---
const stripManualInfo = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return null;
  // supports: "AA-11-BB • manual" or "John Doe • manual"
  const left = s.split("•")[0].trim();
  return left || null;
};

const normalizeExternalDisplay = (row) => {
  const r = row || {};
  const typeUpper = String(r.type || "").toUpperCase();

  const loadCarrierExternal = !!r?.load && isExternalTransportCarrier(r.load);

  const truckInfo =
    typeUpper === "DELIVERY"
      ? (r.truckDeliveryInfo ?? r.load?.truckDeliveryInfo ?? null)
      : (r.truckPickupInfo ?? r.load?.truckPickupInfo ?? null);

  const driverInfo =
    typeUpper === "DELIVERY"
      ? (r.driverDeliveryInfo ?? r.load?.driverDeliveryInfo ?? null)
      : (r.driverPickupInfo ?? r.load?.driverPickupInfo ?? null);

  const trailerInfo =
    typeUpper === "DELIVERY"
      ? (r.trailerDeliveryInfo ?? r.load?.trailerDeliveryInfo ?? null)
      : (r.trailerPickupInfo ?? r.load?.trailerPickupInfo ?? null);

  const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

  const hasTruckIdField = hasOwn(r, "truckId");
  const hasTruckPlateField = hasOwn(r, "truckPlate");
  const hasDriverIdField = hasOwn(r, "driverId");
  const hasDriverNameField = hasOwn(r, "driverName");
  const hasTrailerIdField = hasOwn(r, "trailerId");
  const hasTrailerPlateField = hasOwn(r, "trailerPlate");

  const shouldFallbackTruck =
    loadCarrierExternal &&
    !hasTruckIdField &&
    !hasTruckPlateField;

  const shouldFallbackDriver =
    loadCarrierExternal &&
    !hasDriverIdField &&
    !hasDriverNameField;

  const shouldFallbackTrailer =
    loadCarrierExternal &&
    !hasTrailerIdField &&
    !hasTrailerPlateField;

  const manualTruckPlate = shouldFallbackTruck ? stripManualInfo(truckInfo) : null;
  const manualDriverName = shouldFallbackDriver ? stripManualInfo(driverInfo) : null;
  const manualTrailerPlate = shouldFallbackTrailer ? stripManualInfo(trailerInfo) : null;

  return {
    ...r,
    truckPlate: hasTruckPlateField ? r.truckPlate : (r.truckPlate ?? manualTruckPlate),
    driverName: hasDriverNameField ? r.driverName : (r.driverName ?? manualDriverName),
    trailerPlate: hasTrailerPlateField ? r.trailerPlate : (r.trailerPlate ?? manualTrailerPlate),
  };
};

function getAllPickups(loads, loadId) {
  const load = loads.find((l) => l.id === loadId);
  return load?.pickupDeliveries?.filter((pd) => pd.type === "PICKUP") || [];
}
function getAllDeliveries(loads, loadId) {
  const load = loads.find((l) => l.id === loadId);
  return load?.pickupDeliveries?.filter((pd) => pd.type === "DELIVERY") || [];
}
function getSessionLoads(loads, transportsessionId) {
  const sid = Number(transportsessionId);
  return (loads || []).filter((l) => {
    const lSid =
      l?.transportsessionId ??
      l?.transportSessionId ??
      l?.transportsession_id ??
      l?.transport_session_id ??
      null;

    return lSid != null && Number(lSid) === sid;
  });
}

function getRepresentativeLoadForSession(loads, transportsessionId) {
  const sessionLoads = getSessionLoads(loads, transportsessionId);

  // 1) se só houver 1 load, perfeito
  if (sessionLoads.length === 1) return sessionLoads[0] || null;

  // 2) se houver várias, tenta escolher uma “ativa” / não finalizada
  const notFinal = sessionLoads.find((l) => !isLoadFinalized(l));
  if (notFinal) return notFinal;

  // 3) fallback: primeira
  return sessionLoads[0] || null;
}

const CLOSED_STATUSES = ["COMPLETED", "CANCELLED"];

const isFuelStrategicPoint = (location) =>
  String(location?.type || "").toUpperCase() === "STRATEGICPOINT" &&
  String(location?.strategicPointPurpose || "").toUpperCase() === "FUEL";

const hasTrailerSwapEnabled = (location) => {
  if (!location) return false;

  if (
    location.hasTrailerSwap === true ||
    String(location.hasTrailerSwap || "").toLowerCase() === "true"
  ) {
    return true;
  }

  const arrivalValue =
    location.trailerIdArrival ??
    location.trailerArrivalPlate ??
    "";

  const departureValue =
    location.trailerIdDeparture ??
    location.trailerDeparturePlate ??
    "";

  const arrivalText = String(arrivalValue || "").trim();
  const departureText = String(departureValue || "").trim();

  return !!arrivalText && !!departureText && arrivalText !== departureText;
};

const isLoadFinalized = (load) => {
  const s = String(load?.status ?? "").toUpperCase();
  return s === "COMPLETED" || s === "CANCELLED";
};

const reorderCooldownUntil = new Map(); // transportsessionId -> timestamp (ms)

const TransportSessionLocations = forwardRef(
  (
    {
      transportsessionId,
      refreshLoads,
      loads = [],
      setPendingCmrUploads,
      onCmrFinalized = () => {},
      frozenLoadIdsForSession = [],
      freezeStrategicPoints = false,
      onPointsChanged = () => {},
      isClosingSession = false,
    },
    ref
  ) => {
    const { t, i18n } = useTranslation(["transport_session_locations", "status_selector_modal"]);
    const tStatus = (key, fallback) =>
      t(`status_selector_modal:${key}`, { defaultValue: fallback });

    const tl = (key, fallback, opts = {}) =>
      t(key, { defaultValue: fallback, ...opts });

    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sessionMotionReady, setSessionMotionReady] = useState(false);

    const [modalData, setModalData] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [existingDateHours, setExistingDateHours] = useState([]);

    const listRef = useRef(null);
    const sortableRef = useRef(null);
    const [resetKey, setResetKey] = useState(0);

    const [truckModalOpen, setTruckModalOpen] = useState(false);
    const [selectedPD, setSelectedPD] = useState(null);

    const [trailerModalOpen, setTrailerModalOpen] = useState(false);
    const [selectedPDForTrailer, setSelectedPDForTrailer] = useState(null);

    // NEW CODE BITES
    const [trailersById, setTrailersById] = useState({}); // { [trailerId]: "AA-00-BB" }

    // 🆕 Free-text truck (external carriers)
    const [freeTextTruckModalOpen, setFreeTextTruckModalOpen] = useState(false);
    const [freeTextTruckCtx, setFreeTextTruckCtx] = useState(null);
    // shape: { loadId, type: "PICKUP" | "DELIVERY" }

    // 🆕 Free-text driver (external carriers)
    const [freeTextDriverModalOpen, setFreeTextDriverModalOpen] = useState(false);
    const [freeTextDriverCtx, setFreeTextDriverCtx] = useState(null);
    // shape: { loadId, type: "PICKUP" | "DELIVERY" }

    const [driverModalOpen, setDriverModalOpen] = useState(false);
    const [selectedPDForDriver, setSelectedPDForDriver] = useState(null);
    const [selectedDriverSlot, setSelectedDriverSlot] = useState(1);

    // NEW CODE BITES
    const loadsByIdRef = useRef({});

    // sempre que loads mudar, atualiza o map
    useEffect(() => {
      const map = {};
      for (const l of (loads || [])) {
        if (l?.id != null) map[String(l.id)] = l;
      }
      loadsByIdRef.current = map;
    }, [loads]);

    useEffect(() => {
      if (driverModalOpen) {
        console.log("[TSL][DriverModal] OPEN -> DriverSelectorModal (from TransportSessionLocations.js)", {
          transportsessionId,
          selectedPDForDriverId: selectedPDForDriver?.id,
          selectedPDForDriverType: selectedPDForDriver?.type,
          loadId: selectedPDForDriver?.loadId,
        });
        console.trace("[TSL][DriverModal] stack");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driverModalOpen]);
    
    useEffect(() => {
      if (freeTextDriverModalOpen) {
        console.log("[TSL][DriverModal] OPEN -> FreeTextDriverModal (from TransportSessionLocations.js)", {
          transportsessionId,
          ctx: freeTextDriverCtx,
        });
        console.trace("[TSL][FreeTextDriverModal] stack");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [freeTextDriverModalOpen]);
    

    const [strategicModalOpen, setStrategicModalOpen] = useState(false);

    const [editStrategicModalOpen, setEditStrategicModalOpen] = useState(false);
    const [selectedStrategicPointToEdit, setSelectedStrategicPointToEdit] =
      useState(null);

    const [mapModalOpen, setMapModalOpen] = useState(false);

    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedPDForStatus, setSelectedPDForStatus] = useState(null);

    const locationsRef = useRef([]);
    const isReordering = useRef(false);
    const latestRefreshId = useRef(0);

    const [selectedLoadForCmr, setSelectedLoadForCmr] = useState(null);
    const [cmrLoadModalOpen, setCmrLoadModalOpen] = useState(false);
    const [selectedPdForPhotos, setSelectedPdForPhotos] = useState(null);
    const [pendingInventoryAfterCmr, setPendingInventoryAfterCmr] = useState(null);

    const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
    const [inventoryTruck, setInventoryTruck] = useState(null);
    const [inventoryData, setInventoryData] = useState(null);
    const [inventorySaving, setInventorySaving] = useState(false);

    const [trailerInventoryModalOpen, setTrailerInventoryModalOpen] = useState(false);
    const [trailerMeta, setTrailerMeta] = useState(null);
    const [trailerInvData, setTrailerInvData] = useState(null);
    const [trailerInvSaving, setTrailerInvSaving] = useState(false);

    const cmpLocations = (a, b) => {
      const tA = new Date(`${a.date ?? "1900-01-01"}T${a.hour ?? "00:00"}`).getTime();
      const tB = new Date(`${b.date ?? "1900-01-01"}T${b.hour ?? "00:00"}`).getTime();
      if (tA !== tB) return tA - tB;
      return (a.sequence ?? 0) - (b.sequence ?? 0);
    };

    const sortLocationsSafe = (rows) => {
      const arr = Array.isArray(rows) ? [...rows] : [];
      arr.sort(cmpLocations);
      return arr;
    };

    const safeDefer = (fn, delay = 150) => {
      if (isReordering.current) setTimeout(() => fn(), delay);
      else fn();
    };

    const formatDateLocal = (isoDate) => {
      if (!isoDate) return "";
    
      try {
        const dateOnly = String(isoDate).includes("T")
          ? String(isoDate).split("T")[0]
          : String(isoDate);
    
        const [year, month, day] = dateOnly.split("-");
    
        if (!year || !month || !day) return "";
    
        return `${day}/${month}/${year}`;
      } catch {
        return "";
      }
    };

    const formatStatusEventDateTime = (event) => {
      const raw =
        event?.phoneTimestamp ||
        event?.eventTimestamp ||
        event?.createdAt ||
        null;

      if (!raw) return null;

      try {
        const date = new Date(raw);

        if (Number.isNaN(date.getTime())) return null;

        const locale = i18n.language || "pt-PT";

        const formattedDate = date.toLocaleDateString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });

        const formattedTime = date.toLocaleTimeString(locale, {
          hour: "2-digit",
          minute: "2-digit",
        });

        return `${formattedTime} · ${formattedDate} `;
      } catch {
        return null;
      }
    };

    const formatGpsDistance = (meters) => {
      const n = Number(meters);

      if (!Number.isFinite(n) || n < 0) return null;

      const locale = i18n.language || "pt-PT";

      if (n < 1000) {
        const formattedMeters = Math.round(n).toLocaleString(locale);

        return tl(
          "statusGps.distanceMeters",
          "{{distance}} m de distância",
          { distance: formattedMeters }
        );
      }

      const km = n / 1000;

      const formattedKm = km.toLocaleString(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

      return tl(
        "statusGps.distanceKm",
        "{{distance}} km de distância",
        { distance: formattedKm }
      );
    };

    const joinStatusGpsDetails = (...parts) => {
      return parts
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" · ");
    };

    const getStatusGpsMeta = (location) => {
      const event = location?.latestStatusEvent || null;

      if (!event) {
        return {
          icon: "warning",
          text: tl("statusGps.noEvent", "Sem confirmação GPS"),
          detail: null,
          className: "status-gps-missing",
        };
      }

      const timestampText = formatStatusEventDateTime(event);
      const distanceText = formatGpsDistance(event.distanceFromPlaceMeters);

      const hasGps =
        event.latitude != null &&
        event.longitude != null &&
        String(event.gpsSource || "").toUpperCase() !== "UNAVAILABLE";

      if (!hasGps) {
        return {
          icon: "warning",
          text: tl("statusGps.noGps", "Sem GPS"),
          detail: timestampText,
          className: "status-gps-missing",
        };
      }

      if (event.locationMatch === true) {
        return {
          icon: "location",
          text: tl("statusGps.onLocation", "GPS confirmado no local"),
          detail: timestampText,
          className: "status-gps-ok",
        };
      }

      if (event.locationMatch === false) {
        return {
          icon: "warning",
          text: tl("statusGps.outsideLocation", "GPS fora do local"),
          detail: joinStatusGpsDetails(distanceText, timestampText),
          className: "status-gps-warning",
        };
      }

      return {
        icon: "location",
        text: tl("statusGps.gpsReceived", "GPS recebido"),
        detail: joinStatusGpsDetails(distanceText, timestampText),
        className: "status-gps-neutral",
      };
    };

    const renderStatusGpsIcon = (iconType) => {
      if (iconType === "location") {
        return <FaMapMarkerAlt className="status-gps-svg-icon" aria-hidden="true" />;
      }

      if (iconType === "warning") {
        return <FaExclamationTriangle className="status-gps-svg-icon" aria-hidden="true" />;
      }

      return <FaCheckCircle className="status-gps-svg-icon" aria-hidden="true" />;
    };

    const renderStatusMainIcon = (status) => {
      const upper = String(status || "").toUpperCase();

      if (upper === "COMPLETED") {
        return <FaCheckCircle className="status-main-svg-icon" aria-hidden="true" />;
      }

      if (upper === "CANCELLED") {
        return <FaTimesCircle className="status-main-svg-icon" aria-hidden="true" />;
      }

      return <FaInfoCircle className="status-main-svg-icon" aria-hidden="true" />;
    };

    const renderStatusButtonContent = (location) => {
      const statusText = location.status
        ? statusLabel(location.status)
        : tl("buttons.status", "Status");

      const gpsMeta = getStatusGpsMeta(location);

      return (
        <span className="status-button-content">
          <span className="status-button-main">
            {location.status && renderStatusMainIcon(location.status)}
            <span>{statusText}</span>
          </span>

          {location.status && (
            <span className={`status-gps-line ${gpsMeta.className}`}>
              <span className="status-gps-summary">
                <span className="status-gps-icon">
                  {renderStatusGpsIcon(gpsMeta.icon)}
                </span>

                <span>{gpsMeta.text}</span>
              </span>

              {gpsMeta.detail && (
                <span className="status-gps-detail">
                  {gpsMeta.detail}
                </span>
              )}
            </span>
          )}
        </span>
      );
    };

    const getDriverDisplayName = (driver) => {
      if (!driver) return null;
      const fromParts = `${driver.firstName ?? ""} ${driver.lastName ?? ""}`.trim();
      return (
        driver.name ||
        driver.fullName ||
        driver.driverName ||
        (fromParts ? fromParts : null) ||
        driver.email ||
        null
      );
    };

    function softRefresh() {
      if (isReordering.current) return;
      const refreshId = ++latestRefreshId.current;

      axios
        .get(`/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`)
        .then((res) => {
          if (refreshId !== latestRefreshId.current) return;
          const rows = Array.isArray(res.data) ? res.data : [];
          const prevSnap = locationsRef.current || [];

          const normalized = rows.map((r) => {
            const loadFallback = loadsByIdRef.current?.[String(r?.loadId)] || null;

            const prevLoc = prevSnap.find((p) => Number(p?.id) === Number(r?.id)) || null;
            const merged = mergeStrategicFromPrev(r, prevLoc);

            return normalizeExternalDisplay({
              ...merged,
              load: merged?.load ?? loadFallback,
            });
          });
          const sorted = sortLocationsSafe(normalized);

          setLocations(sorted);
          setResetKey((k) => k + 1);

          // ✅ CRITICAL: avisa o Loads.js para recalcular o progress instantaneamente
          onPointsChanged?.(sorted);
        })
        .catch(() => {});
    }

    // ✅ helper: preserva campos de StrategicPoint se /light não devolver tudo
    const mergeStrategicFromPrev = (row, prevLoc) => {
      const t = String(row?.type || "").toUpperCase();
      if (t !== "STRATEGICPOINT") return row;

      const out = { ...row };

      if (!out.strategicPointPurpose && prevLoc?.strategicPointPurpose) {
        out.strategicPointPurpose = prevLoc.strategicPointPurpose;
      }

      if (out.hasTrailerSwap == null && prevLoc?.hasTrailerSwap != null) {
        out.hasTrailerSwap = prevLoc.hasTrailerSwap;
      }

      // TRUCK arrival/departure
      if (out.truckIdArrival == null && prevLoc?.truckIdArrival != null) out.truckIdArrival = prevLoc.truckIdArrival;
      if (!out.truckArrivalPlate && prevLoc?.truckArrivalPlate) out.truckArrivalPlate = prevLoc.truckArrivalPlate;

      if (out.truckIdDeparture == null && prevLoc?.truckIdDeparture != null) out.truckIdDeparture = prevLoc.truckIdDeparture;
      if (!out.truckDeparturePlate && prevLoc?.truckDeparturePlate) out.truckDeparturePlate = prevLoc.truckDeparturePlate;

      // TRAILER arrival/departure
      if (out.trailerIdArrival == null && prevLoc?.trailerIdArrival != null) out.trailerIdArrival = prevLoc.trailerIdArrival;
      if (!out.trailerArrivalPlate && prevLoc?.trailerArrivalPlate) out.trailerArrivalPlate = prevLoc.trailerArrivalPlate;

      if (out.trailerIdDeparture == null && prevLoc?.trailerIdDeparture != null) out.trailerIdDeparture = prevLoc.trailerIdDeparture;
      if (!out.trailerDeparturePlate && prevLoc?.trailerDeparturePlate) out.trailerDeparturePlate = prevLoc.trailerDeparturePlate;

      // DRIVER 1 arrival/departure
      if (out.driverIdArrival == null && prevLoc?.driverIdArrival != null) out.driverIdArrival = prevLoc.driverIdArrival;
      if (!out.driverNameArrival && prevLoc?.driverNameArrival) out.driverNameArrival = prevLoc.driverNameArrival;

      if (out.driverIdDeparture == null && prevLoc?.driverIdDeparture != null) out.driverIdDeparture = prevLoc.driverIdDeparture;
      if (!out.driverNameDeparture && prevLoc?.driverNameDeparture) out.driverNameDeparture = prevLoc.driverNameDeparture;

      // DRIVER 2 arrival/departure
      if (out.driver2IdArrival == null && prevLoc?.driver2IdArrival != null) out.driver2IdArrival = prevLoc.driver2IdArrival;
      if (!out.driver2NameArrival && prevLoc?.driver2NameArrival) out.driver2NameArrival = prevLoc.driver2NameArrival;

      if (out.driver2IdDeparture == null && prevLoc?.driver2IdDeparture != null) out.driver2IdDeparture = prevLoc.driver2IdDeparture;
      if (!out.driver2NameDeparture && prevLoc?.driver2NameDeparture) out.driver2NameDeparture = prevLoc.driver2NameDeparture;

      return out;
    };

    // Mantém todos os campos que a resposta compacta do reorder não devolve
    // (coordenadas, morada, load enriquecida, etc.). Isto evita que o mapa
    // perca pontos enquanto aguarda o refresh por socket.
    const mergeReorderRowsWithSnapshot = (rows, snapshot) => {
      const safeRows = Array.isArray(rows) ? rows : [];
      const safeSnapshot = Array.isArray(snapshot) ? snapshot : [];

      return safeRows.map((row) => {
        const prevLoc =
          safeSnapshot.find(
            (point) => Number(point?.id) === Number(row?.id)
          ) || null;

        const loadFallback =
          loadsByIdRef.current?.[String(row?.loadId)] ||
          prevLoc?.load ||
          null;

        const merged = mergeStrategicFromPrev(
          {
            ...prevLoc,
            ...row,
          },
          prevLoc
        );

        return normalizeExternalDisplay({
          ...merged,
          load: merged?.load ?? loadFallback,
        });
      });
    };

    const fetchLocations = async () => {
      const refreshId = ++latestRefreshId.current;
      if (isReordering.current) return;
      if (!transportsessionId) return;

      const until = reorderCooldownUntil.get(transportsessionId) || 0;

      const doFetch = async () => {
        const response = await axios.get(
          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
        );
        if (refreshId !== latestRefreshId.current) return;

        const rows = Array.isArray(response.data) ? response.data : [];
        const prevSnap = locationsRef.current || [];

        const normalized = rows.map((r) => {
          const loadFallback = loadsByIdRef.current?.[String(r?.loadId)] || null;

          const prevLoc = prevSnap.find((p) => Number(p?.id) === Number(r?.id)) || null;
          const merged = mergeStrategicFromPrev(r, prevLoc);

          return normalizeExternalDisplay({
            ...merged,
            load: merged?.load ?? loadFallback,
          });
        });
        const sorted = sortLocationsSafe(normalized);

        setExistingDateHours(sorted.map((loc) => `${loc.date}T${loc.hour}`));
        setLocations(sorted);
        onPointsChanged?.(sorted);
      };

      try {
        await doFetch();
        if (Date.now() < until) {
          setTimeout(() => {
            doFetch().catch(() => {});
          }, 400);
        }
      } catch (error) {
        console.error("❌ Error fetching pickup/delivery locations:", error);
      }
    };

    // NEW CODE BITES
    useEffect(() => {
      // mantém um map trailerId -> plate para o timeline render
      axios
        .get("/pickupdeliveries/trailers/with-latest-assignments", { params: { _: Date.now() } })
        .then((res) => {
          const rows = Array.isArray(res.data) ? res.data : [];
          const map = {};
          for (const tr of rows) {
            const plate = tr?.trailerPlate || tr?.trailerNumber || null;
            if (tr?.id && plate) map[String(tr.id)] = plate;
          }
          setTrailersById(map);
        })
        .catch(() => {});
    }, []);

    useEffect(() => {
      locationsRef.current = locations;
    }, [locations]);

    useEffect(() => {
      setSessionMotionReady(false);

      let raf1 = null;
      let raf2 = null;

      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          setSessionMotionReady(true);
        });
      });

      return () => {
        if (raf1) cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    }, [transportsessionId]);

    useEffect(() => {
      fetchLocations();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transportsessionId]);

    // 🔌 WS: live updates for this transport session (auth-safe)
    useEffect(() => {
      if (!transportsessionId) return;
      const s = getSocket();

      s.emit("subscribe:session", { id: transportsessionId });

      const refreshNow = () => safeDefer(() => softRefresh(), 120);
      const onCreated = () => refreshNow();
      const onUpdated = () => refreshNow();
      const onReordered = () => refreshNow();
      const onDeleted = () => refreshNow();
      const onStatus = () => refreshNow();
      const onStale = () => refreshNow();

      s.on("pd:created", onCreated);
      s.on("pd:updated", onUpdated);
      s.on("pd:reordered", onReordered);
      s.on("pd:deleted", onDeleted);
      s.on("pd:status", onStatus);
      s.on("session:points:stale", onStale);

      return () => {
        s.emit("unsubscribe:session", { id: transportsessionId });
        s.off("pd:created", onCreated);
        s.off("pd:updated", onUpdated);
        s.off("pd:reordered", onReordered);
        s.off("pd:deleted", onDeleted);
        s.off("pd:status", onStatus);
        s.off("session:points:stale", onStale);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transportsessionId]);

useEffect(() => {
  function onTruckAssigned(e) {
    const { pickupDeliveryId, truck, truckRole } = e.detail || {};
    if (!pickupDeliveryId || !truck) return;

    const clicked = (locationsRef.current || []).find((l) => Number(l.id) === Number(pickupDeliveryId));
    if (!clicked || isAssignmentBlockedNoAlert(clicked)) return;

    setLocations((prev) =>
      prev.map((loc) => {
        if (Number(loc.id) !== Number(pickupDeliveryId)) return loc;

        const plate = truck?.plate ?? loc.truckPlate ?? null;

        if (String(loc.type || "").toUpperCase() === "STRATEGICPOINT") {
          if (truckRole === "arrival") {
            return {
              ...loc,
              truckIdArrival: truck?.id ?? null,
              truckArrivalPlate: plate,
            };
          }

          if (truckRole === "departure") {
            return {
              ...loc,
              truckIdDeparture: truck?.id ?? null,
              truckDeparturePlate: plate,
            };
          }

          return loc;
        }

        return {
          ...loc,
          truckId: truck?.id ?? null,
          truckPlate: plate,
        };
      })
    );

    setResetKey((k) => k + 1);
    setTimeout(() => softRefresh(), 150);
  }

  window.addEventListener("truck-assigned", onTruckAssigned);
  return () => window.removeEventListener("truck-assigned", onTruckAssigned);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


    // ✅ Only freeze strategic points when the WHOLE session is closed
    const effectiveFreezeStrategicPoints =
      !!freezeStrategicPoints &&
      locations.length > 0 &&
      locations.every((l) => CLOSED_STATUSES.includes(l.status));

    const locationsWithLock = (() => {
      const base = sortLocationsSafe(locations); // ✅ canonical chronological order
      const lockedLocations = [];

      const closedIndices = base
        .map((loc, index) => (CLOSED_STATUSES.includes(loc.status) ? index : -1))
        .filter((index) => index !== -1);

      const lastClosedIndex = closedIndices[closedIndices.length - 1] ?? -1;
      const lastTimelineId = base.length ? base[base.length - 1].id : null;

      let canEditNextPending = true;

      base.forEach((loc, index) => {
        // Strategic points: follow timeline-lock rules for STATUS;
        // but FREEZE only if the session is fully closed.
        if (loc.type === "STRATEGICPOINT") {
          const isClosed = CLOSED_STATUSES.includes(loc.status);

          let isLocked = false;

          if (isClosed && closedIndices.length > 1) {
            isLocked = index !== lastClosedIndex;
          } else if (!isClosed) {
            if (index > lastClosedIndex && canEditNextPending) {
              isLocked = false;
              canEditNextPending = false;
            } else {
              isLocked = true;
            }
          } else {
            isLocked = false;
          }

          const shouldFreezeStrategic =
            effectiveFreezeStrategicPoints && loc.id !== lastTimelineId;

          lockedLocations.push({
            ...loc,
            isStatusLocked: isLocked,
            isFrozen: shouldFreezeStrategic,
            isLocked: false,
          });
          return;
        }

        const isFrozen = loc.loadId && frozenLoadIdsForSession.includes(loc.loadId);
        if (isFrozen) {
          lockedLocations.push({ ...loc, isLocked: true, isFrozen: true });
          return;
        }

        const isClosed = CLOSED_STATUSES.includes(loc.status);

        if (isClosed && closedIndices.length > 1) {
          lockedLocations.push({ ...loc, isLocked: index !== lastClosedIndex });
          return;
        }

        if (!isClosed) {
          if (index > lastClosedIndex && canEditNextPending) {
            lockedLocations.push({ ...loc, isLocked: false });
            canEditNextPending = false;
          } else {
            lockedLocations.push({ ...loc, isLocked: true });
          }
          return;
        }

        lockedLocations.push({ ...loc, isLocked: false });
      });

      return lockedLocations;
    })();

    const updatePickupTrucksForLoad = (loadId, truck) => {
      setLocations((prev) =>
        prev.map((loc) =>
          loc.loadId === loadId && loc.type === "PICKUP"
            ? { ...loc, truckId: truck.id, truckPlate: truck.plate ?? null }
            : loc
        )
      );
      setResetKey((prev) => prev + 1);

      setTimeout(() => {
        fetchLocations().then(() => setResetKey((prev) => prev + 1));
      }, 300);
    };

    const updateDeliveryTrucksForLoad = (loadId, truck) => {
      setLocations((prev) =>
        prev.map((loc) =>
          loc.loadId === loadId && loc.type === "DELIVERY"
            ? { ...loc, truckId: truck.id, truckPlate: truck.plate ?? null }
            : loc
        )
      );
      setResetKey((prev) => prev + 1);

      setTimeout(() => {
        fetchLocations().then(() => setResetKey((prev) => prev + 1));
      }, 300);
    };

    useImperativeHandle(ref, () => ({
      refreshLocations: fetchLocations,
      updatePickupTrucksForLoad,
      updateDeliveryTrucksForLoad,
    }));

    /* ------------------------------ Inventory finalize ------------------------------ */
    const finalizeAfterInventory = async (loadId) => {
      if (!loadId) return;

      // ✅ UI instantâneo: fecha contexto e remove já do Current Loads
      try {
        setSelectedLoadForCmr(loadId);

        // remove “pendentes” locais já
        if (setPendingCmrUploads) {
          setPendingCmrUploads((prev) => {
            const next = new Set(prev);
            next.delete(loadId);
            return next;
          });
        }

        // ✅ Emula o socket "loads:deleted" IMEDIATAMENTE (UI some já)
        // (Loads.js pode ouvir isto e remover do state)
        try {
          window.dispatchEvent(new CustomEvent("loads:deleted:optimistic", { detail: { id: loadId } }));
        } catch {}

        // ✅ Fecha modais para não ficar “a rodar”
        setInventoryModalOpen(false);
        setTrailerInventoryModalOpen(false);
        setCmrLoadModalOpen(false);

        // ✅ Agora sim: chama o backend
        await axios.post(`/loads/${loadId}/finalize-cmr`);

        // Confirmações leves (não bloqueia UX)
        setTimeout(() => {
          fetchLocations();
          if (refreshLoads) refreshLoads();
        }, 50);

        onCmrFinalized?.(loadId);
      } catch (err) {
        console.error("❌ Failed to finalize after inventory:", err);

        // ❗fallback: se falhar, faz refresh para “repor” o que removeste
        try {
          if (refreshLoads) await refreshLoads();
          await fetchLocations();
        } catch {}

        alert(tl("alerts.cmrUploadFinalizeFailed", "❌ Failed to upload/finalize CMR."));
      } finally {
        setSelectedLoadForCmr(null);
      }
    };

    /* ------------------------------ Modals routing ------------------------------ */
    const openTruckAssignUI = async (loc, truckRole) => {
      if (!loc || guardAssignmentAction(loc)) {
        dbgTruck("[TSL][openTruckAssignUI] blocked (missing loc / frozen / closed)", { loc });
        return;
      }

      dbgTruck("[TSL][openTruckAssignUI] start", {
        transportsessionId,
        pdId: loc?.id,
        loadId: loc?.loadId,
        type: loc?.type,
        truckRole,
      });

      // ✅ Strategic points: always internal selector
      if (loc.type === "STRATEGICPOINT") {
        const repLoad = getRepresentativeLoadForSession(loads, transportsessionId);

        setSelectedPD({
          ...loc,
          truckRole: truckRole || "arrival",
          load: repLoad || null, // ✅ CRITICAL: dá contexto ao modal
          isExternalCarrier: repLoad ? isExternalTransportCarrier(repLoad) : false,
        });

        setTruckModalOpen(true);
        return;
      }

      let load = (loads || []).find((l) => Number(l.id) === Number(loc.loadId));

      // ✅ Fallback: if loads[] doesn't include it, fetch the load so we can route correctly
      if (!load && loc?.loadId) {
        try {
          dbgTruck("[TSL][openTruckAssignUI] load not in props -> fetching /loads/:id", {
            loadId: loc.loadId,
          });

          const res = await axios.get(`/loads/${Number(loc.loadId)}`, {
            validateStatus: () => true,
          });

          if (res?.status === 200) {
            load = res.data?.load ?? res.data?.row ?? res.data ?? null;
          }

          dbgTruck("[TSL][openTruckAssignUI] fetched load result", {
            ok: res?.status,
            hasLoad: !!load,
          });
        } catch (e) {
          dbgTruck("[TSL][openTruckAssignUI] fetch load failed", e);
        }
      }

      // ✅ IMPORTANT: In TransportSessionLocations we ALWAYS use TruckSelectorModal
      // (both internal and subcontracted loads)
      if (load && isExternalTransportCarrier(load)) {
        dbgTruck("[TSL][openTruckAssignUI] external -> TruckSelectorModal (forced)", {
          pdId: loc?.id,
          loadId: loc?.loadId,
          type: loc?.type,
        });
        // no return; continue to open the normal selector
      }

      dbgTruck("[TSL][openTruckAssignUI] internal -> TruckSelectorModal", { pdId: loc?.id });
      setSelectedPD({
        ...loc,
        load: load || loc.load || null, // ✅ keep resolved load for modal props
        truckRole: truckRole || loc.type.toLowerCase(),
      });
      setTruckModalOpen(true);
    };

    const openDriverAssignUI = async (loc, driverRole, driverSlot = 1) => {
      if (!loc || guardAssignmentAction(loc)) {
        dbgDriver("[TSL][openDriverAssignUI] blocked (missing loc / frozen / closed)", { loc });
        return;
      }
    
      dbgDriver("[TSL][openDriverAssignUI] CLICK -> routing driver modal", {
        file: "src/components/TransportSessionLocations.js",
        transportsessionId,
        pdId: loc?.id,
        loadId: loc?.loadId,
        type: loc?.type,
        driverRole,
        driverSlot,
      });
      if (localStorage.getItem("debugDriverRouting") === "1") {
        console.trace("[TSL][openDriverAssignUI] stack");
      }
    
      // ✅ Strategic points: mantém selector normal (interno)
      if (loc.type === "STRATEGICPOINT") {
        const repLoad = getRepresentativeLoadForSession(loads, transportsessionId);

        setSelectedPDForDriver({
          ...loc,
          driverRole: driverRole || "arrival",
          load: repLoad || null,
          isExternalCarrier: repLoad ? isExternalTransportCarrier(repLoad) : false,
        });

        setSelectedDriverSlot(driverSlot);
        setDriverModalOpen(true);
        return;
      }
    
      let load = (loads || []).find((l) => Number(l.id) === Number(loc.loadId));
    
      // fallback: se não tiveres o load no props, vai buscar
      if (!load && loc?.loadId) {
        try {
          dbgDriver("[TSL][openDriverAssignUI] load not in props -> fetching /loads/:id", {
            loadId: loc.loadId,
          });
    
          const res = await axios.get(`/loads/${Number(loc.loadId)}`, { validateStatus: () => true });
          if (res?.status === 200) load = res.data?.load ?? res.data?.row ?? res.data ?? null;
    
          dbgDriver("[TSL][openDriverAssignUI] fetched load result", {
            ok: res?.status,
            hasLoad: !!load,
          });
        } catch (e) {
          dbgDriver("[TSL][openDriverAssignUI] fetch load failed", e);
        }
      }
    
      // ✅ Carrier externo -> free-text driver modal
      // ✅ IMPORTANT: In TransportSessionLocations we ALWAYS use DriverSelectorModal
      // (both internal and subcontracted loads)
      if (load && isExternalTransportCarrier(load)) {
        dbgDriver("[TSL][openDriverAssignUI] external -> DriverSelectorModal (forced)", {
          pdId: loc?.id,
          loadId: loc?.loadId,
          type: loc?.type,
        });
        // no return; continue to open the normal selector
      }
    
      // ✅ interno -> DriverSelectorModal
      dbgDriver("[TSL][openDriverAssignUI] internal -> DriverSelectorModal", {
        pdId: loc?.id,
        driverRole,
      });
    
      setSelectedPDForDriver({
        ...loc,
        load: load || loc.load || null, // ✅ keep resolved load for modal props
        driverRole,
      });
      setSelectedDriverSlot(driverSlot);
      setDriverModalOpen(true);
    };
    
    const openTrailerAssignUI = async (loc, trailerRole) => {
      if (!loc || guardAssignmentAction(loc)) return;

      // ✅ Strategic points: open trailer selector with role (arrival/departure)
      if (String(loc.type || "").toUpperCase() === "STRATEGICPOINT") {
        const repLoad = getRepresentativeLoadForSession(loads, transportsessionId);

        setSelectedPDForTrailer({
          ...loc,
          trailerRole: trailerRole || "arrival",
          load: repLoad || null, // ✅
          isExternalCarrier: repLoad ? isExternalTransportCarrier(repLoad) : false,
        });

        setTrailerModalOpen(true);
        return;
      }

      let load = (loads || []).find((l) => Number(l.id) === Number(loc.loadId));

      // fallback: fetch load if missing
      if (!load && loc?.loadId) {
        try {
          const res = await axios.get(`/loads/${Number(loc.loadId)}`, { validateStatus: () => true });
          if (res?.status === 200) load = res.data?.load ?? res.data?.row ?? res.data ?? null;
        } catch {}
      }

      const enriched = { ...loc, load: load || loc.load || null };
      setSelectedPDForTrailer(enriched);
      setTrailerModalOpen(true);
    };

    /* ------------------------------ Reorder ------------------------------ */
    const persistReorder = async (newOrder) => {
      const previousSnapshot = locationsRef.current || [];
      const optimistic = newOrder.map((point) => ({
        ...point,
        sequence: point.newSequence,
      }));

      try {
        setIsLoading(true);
        setLocations(optimistic);
        onPointsChanged?.(optimistic);
        setResetKey((key) => key + 1);

        reorderCooldownUntil.set(transportsessionId, Date.now() + 2000);

        const { data } = await axios.put("/pickupdeliveries/reorder", {
          transportsessionId,
          updatedSequence: newOrder,
        });

        if (Array.isArray(data?.rows)) {
          const syncedRows = mergeReorderRowsWithSnapshot(
            data.rows,
            optimistic
          );

          setLocations(syncedRows);
          onPointsChanged?.(syncedRows);
          setResetKey((key) => key + 1);
        } else {
          await fetchLocations();
        }

        try {
          refreshLoads?.();
        } catch (refreshError) {
          console.warn(
            "[TransportSessionLocations] refresh after reorder failed:",
            refreshError?.message
          );
        }

        return true;
      } catch (error) {
        console.error("❌ Update failed:", error);
        setLocations(previousSnapshot);
        onPointsChanged?.(previousSnapshot);
        setResetKey((key) => key + 1);

        alert(
          tl(
            "alerts.reorderFailed",
            "Não foi possível guardar a nova ordem. Tenta novamente."
          )
        );

        return false;
      } finally {
        sortableRef.current?.option("disabled", false);
        isReordering.current = false;
        setIsLoading(false);
      }
    };

    const cancelReorder = (message) => {
      if (message) alert(message);
      sortableRef.current?.option("disabled", false);
      isReordering.current = false;
      setResetKey((k) => k + 1);
      setIsLoading(false);
    };

    const handleReorder = async (evt) => {
      isReordering.current = true;

      const locationItems = Array.from(listRef.current.querySelectorAll(".location-item"));

      const draggedEl = evt.item;
      const draggedId = parseInt(draggedEl.getAttribute("data-id"), 10);
      const draggedOriginal = locationsRef.current.find((loc) => loc.id === draggedId);

      // (1) Block reorder if COMPLETED/CANCELLED
      if (["COMPLETED", "CANCELLED"].includes(String(draggedOriginal?.status || "").toUpperCase())) {
        const statusName = tl(
          `statuses.${String(draggedOriginal?.status || "").toUpperCase()}`,
          String(draggedOriginal?.status || "")
        );

        return cancelReorder(
          tl(
            "alerts.cannotMoveBecauseStatus",
            `❌ Cannot move this point because status is "{{status}}".`,
            { status: statusName }
          )
        );
      }

      // (2) Block reorder if moving next to a COMPLETED/CANCELLED item
      const locationIds = locationItems.map((el) => parseInt(el.getAttribute("data-id"), 10));
      const newIndex = locationIds.indexOf(draggedId);
      const nextId = locationIds[newIndex + 1];
      const nextItem = locationsRef.current.find((loc) => loc.id === nextId);

      if (["COMPLETED", "CANCELLED"].includes(String(nextItem?.status || "").toUpperCase())) {
        const statusName = tl(
          `statuses.${String(nextItem?.status || "").toUpperCase()}`,
          String(nextItem?.status || "")
        );

        return cancelReorder(
          tl(
            "alerts.cannotMoveBecauseStatus",
            `❌ Cannot move this point because status is "{{status}}".`,
            { status: statusName }
          )
        );
      }

      // Safety: ensure DOM IDs match latest snapshot
      const latestLocations = locationsRef.current;
      const allIds = latestLocations.map((l) => l.id);

      for (const el of locationItems) {
        const id = parseInt(el.getAttribute("data-id"), 10);
        if (!allIds.includes(id)) {
          return cancelReorder(
            tl(
              "alerts.dragCancelledRefreshed",
              "Please try again. Data has refreshed. (Drag cancelled for safety)"
            )
          );
        }
      }

      const newOrder = locationItems.map((el, index) => {
        const id = parseInt(el.getAttribute("data-id"), 10);
        const original = latestLocations.find((l) => l.id === id);
        if (!original) throw new Error(`Could not find PD for id ${id}`);

        const isStrategicPoint =
          String(original?.type || "").toUpperCase() === "STRATEGICPOINT";

        return {
          ...original,
          id,
          newSequence: index + 1,
          truckId: original.truckId ?? null,

          // ✅ Preserve FUEL/GENERAL when reordering strategic points.
          ...(isStrategicPoint
            ? {
                strategicPointPurpose:
                  String(original?.strategicPointPurpose || "GENERAL").toUpperCase() === "FUEL"
                    ? "FUEL"
                    : "GENERAL",
                hasTrailerSwap: !!original?.hasTrailerSwap,
              }
            : {}),
        };
      });

      // (3) Rule: DELIVERY cannot come before PICKUP of same load
      const deliveryBeforePickupViolation = newOrder.some((currentItem, index) => {
        if (currentItem.type !== "DELIVERY") return false;
        const deliveryLoadId = currentItem.loadId;
        return newOrder
          .slice(index + 1)
          .some((nextItem) => nextItem.type === "PICKUP" && nextItem.loadId === deliveryLoadId);
      });

      if (deliveryBeforePickupViolation) {
        return cancelReorder(
          tl(
            "alerts.deliveryBeforePickup",
            "❌ You cannot place a DELIVERY before its corresponding PICK-UP from the same load."
          )
        );
      }

      // (4) Check date/hour conflicts with previous and next
      const draggedItem = newOrder.find(
        (item) => item.id.toString() === evt.item.getAttribute("data-id")
      );
      const newIndex2 = newOrder.findIndex((item) => item.id === draggedItem?.id);
      const prev = newOrder[newIndex2 - 1];
      const next = newOrder[newIndex2 + 1];

      const draggedDateTime = draggedItem?.date
        ? new Date(`${draggedItem.date}T${draggedItem.hour || "00:00"}`)
        : null;
      const prevDateTime = prev?.date ? new Date(`${prev.date}T${prev.hour || "00:00"}`) : null;
      const nextDateTime = next?.date ? new Date(`${next.date}T${next.hour || "23:59"}`) : null;

      let dateConflict = false;
      if (prevDateTime && draggedDateTime && draggedDateTime < prevDateTime) dateConflict = true;
      if (nextDateTime && draggedDateTime && draggedDateTime > nextDateTime) dateConflict = true;

      if (dateConflict) {
        // drag is done; we can allow refresh while modal is open
        isReordering.current = false;

        const modalPayload = {
          draggedItem,
          newOrder,
          newIndex: newIndex2,
          prevDate: prev?.date || null,
          prevHour: prev?.hour || "00:00",
          nextDate: next?.date || null,
          nextHour: next?.hour || "23:59",
        };
        setModalData(modalPayload);
        setShowModal(true);
        return;
      }

      // (5) A timeline e o mapa usam exatamente a mesma persistência.
      await persistReorder(newOrder);
    };

    const handleMapReorder = async ({ orderedIds, draggedId }) => {
      isReordering.current = true;

      const rejectMapReorder = (message) => {
        if (message) alert(message);
        isReordering.current = false;
        return false;
      };

      const latestLocations = locationsRef.current || [];
      const normalizedOrderedIds = Array.isArray(orderedIds)
        ? orderedIds.map(String)
        : [];
      const latestIds = latestLocations.map((point) => String(point.id));

      if (
        normalizedOrderedIds.length !== latestIds.length ||
        new Set(normalizedOrderedIds).size !== latestIds.length ||
        latestIds.some((id) => !normalizedOrderedIds.includes(id))
      ) {
        return rejectMapReorder(
          tl(
            "alerts.dragCancelledRefreshed",
            "Please try again. Data has refreshed. (Drag cancelled for safety)"
          )
        );
      }

      const draggedOriginal = latestLocations.find(
        (point) => String(point.id) === String(draggedId)
      );
      const draggedStatus = String(
        draggedOriginal?.status || ""
      ).toUpperCase();

      if (
        draggedOriginal?.isFrozen ||
        ["COMPLETED", "CANCELLED"].includes(draggedStatus)
      ) {
        const statusName = draggedStatus
          ? tl(`statuses.${draggedStatus}`, draggedStatus)
          : tl("tooltips.statusLocked", "Editing is locked");

        return rejectMapReorder(
          tl(
            "alerts.cannotMoveBecauseStatus",
            `❌ Cannot move this point because status is "{{status}}".`,
            { status: statusName }
          )
        );
      }

      const draggedIndex = normalizedOrderedIds.indexOf(String(draggedId));
      const nextId = normalizedOrderedIds[draggedIndex + 1];
      const nextItem = latestLocations.find(
        (point) => String(point.id) === String(nextId)
      );
      const nextStatus = String(nextItem?.status || "").toUpperCase();

      if (["COMPLETED", "CANCELLED"].includes(nextStatus)) {
        return rejectMapReorder(
          tl(
            "alerts.cannotMoveBecauseStatus",
            `❌ Cannot move this point because status is "{{status}}".`,
            {
              status: tl(`statuses.${nextStatus}`, nextStatus),
            }
          )
        );
      }

      const newOrder = normalizedOrderedIds.map((id, index) => {
        const original = latestLocations.find(
          (point) => String(point.id) === id
        );

        if (!original) {
          throw new Error(`Could not find PD for id ${id}`);
        }

        const isStrategicPoint =
          String(original.type || "").toUpperCase() === "STRATEGICPOINT";

        return {
          ...original,
          id: original.id,
          newSequence: index + 1,
          truckId: original.truckId ?? null,
          ...(isStrategicPoint
            ? {
                strategicPointPurpose:
                  String(
                    original.strategicPointPurpose || "GENERAL"
                  ).toUpperCase() === "FUEL"
                    ? "FUEL"
                    : "GENERAL",
                hasTrailerSwap: !!original.hasTrailerSwap,
              }
            : {}),
        };
      });

      const deliveryBeforePickupViolation = newOrder.some(
        (currentItem, index) => {
          if (String(currentItem.type || "").toUpperCase() !== "DELIVERY") {
            return false;
          }

          return newOrder.slice(index + 1).some((nextPoint) => {
            return (
              String(nextPoint.type || "").toUpperCase() === "PICKUP" &&
              String(nextPoint.loadId) === String(currentItem.loadId)
            );
          });
        }
      );

      if (deliveryBeforePickupViolation) {
        return rejectMapReorder(
          tl(
            "alerts.deliveryBeforePickup",
            "❌ You cannot place a DELIVERY before its corresponding PICK-UP from the same load."
          )
        );
      }

      const draggedItem = newOrder.find(
        (point) => String(point.id) === String(draggedId)
      );
      const newIndex = newOrder.findIndex(
        (point) => String(point.id) === String(draggedId)
      );
      const prev = newOrder[newIndex - 1];
      const next = newOrder[newIndex + 1];

      const draggedDateTime = draggedItem?.date
        ? new Date(`${draggedItem.date}T${draggedItem.hour || "00:00"}`)
        : null;
      const prevDateTime = prev?.date
        ? new Date(`${prev.date}T${prev.hour || "00:00"}`)
        : null;
      const nextDateTime = next?.date
        ? new Date(`${next.date}T${next.hour || "23:59"}`)
        : null;

      const hasDateConflict =
        (prevDateTime &&
          draggedDateTime &&
          draggedDateTime < prevDateTime) ||
        (nextDateTime &&
          draggedDateTime &&
          draggedDateTime > nextDateTime);

      if (hasDateConflict) {
        // O DateConflictModal já é a UI canónica desta regra. Fechamos o mapa
        // apenas enquanto ele está visível para evitar dois backdrops empilhados;
        // no fim, o mapa reabre já com a ordem correta (ou restaurada).
        setMapModalOpen(false);

        return new Promise((resolve) => {
          setModalData({
            draggedItem,
            newOrder,
            newIndex,
            prevDate: prev?.date || null,
            prevHour: prev?.hour || "00:00",
            nextDate: next?.date || null,
            nextHour: next?.hour || "23:59",
            resolveReorder: (saved) => {
              setMapModalOpen(true);
              resolve(saved);
            },
          });
          setShowModal(true);
        });
      }

      return persistReorder(newOrder);
    };

    const handleDateModalConfirm = async (newDate, newHour) => {
      if (!modalData) return;

      const newFull = new Date(`${newDate}T${newHour}`);
      const prevFull =
        modalData.prevDate && modalData.prevHour
          ? new Date(`${modalData.prevDate}T${modalData.prevHour}`)
          : null;
      const nextFull =
        modalData.nextDate && modalData.nextHour
          ? new Date(`${modalData.nextDate}T${modalData.nextHour}`)
          : null;

      if ((prevFull && newFull < prevFull) || (nextFull && newFull > nextFull)) {
        alert(
          tl(
            "alerts.dateMustBeBetween",
            "❌ The new date and hour must be between the previous and next limits."
          )
        );
        return;
      }

      const updatedDragged = {
        ...modalData.draggedItem,
        date: newDate,
        hour: newHour,
      };

      const updatedOrder = [...modalData.newOrder];
      updatedOrder[modalData.newIndex] = updatedDragged;
      const pendingResolve = modalData.resolveReorder;

      setShowModal(false);
      setModalData(null);

      const saved = await persistReorder(updatedOrder);
      pendingResolve?.(saved);
    };

    const handleCancelModal = async () => {
      const pendingResolve = modalData?.resolveReorder;

      // drag is done; ensure we re-enable refresh
      isReordering.current = false;

      setShowModal(false);
      setModalData(null);

      try {
        const getResp = await axios.get(
          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
        );
        const sorted = Array.isArray(getResp.data) ? getResp.data : [];

        setLocations(sorted);
        onPointsChanged?.(sorted);
        setResetKey((prev) => prev + 1);

        if (listRef.current) listRef.current.innerHTML = "";

        if (sortableRef.current) {
          sortableRef.current.destroy();
          sortableRef.current = null;
        }

        setTimeout(() => {
          if (listRef.current && !sortableRef.current) {
            sortableRef.current = Sortable.create(listRef.current, {
              animation: 150,
              handle: ".drag-handle",
              draggable: effectiveFreezeStrategicPoints
                ? ".location-item:not(.frozen):not([data-type='STRATEGICPOINT'])"
                : ".location-item:not(.frozen)",
              ghostClass: "sortable-ghost",
              chosenClass: "sortable-chosen",
              swapThreshold: 0.65,
              onStart: () => {
                isReordering.current = true;
              },
              onEnd: (evt) => {
                setTimeout(() => {
                  handleReorder(evt);
                }, 0);
              },
            });
          }
        }, 100);
      } catch (error) {
        console.error("❌ Failed to restore locations after cancel:", error);
      } finally {
        pendingResolve?.(false);
      }
    };

    useEffect(() => {
      if (sortableRef.current) {
        sortableRef.current.destroy();
        sortableRef.current = null;
      }

      if (listRef.current) {
        sortableRef.current = Sortable.create(listRef.current, {
          animation: 150,
          handle: ".drag-handle",
          draggable: effectiveFreezeStrategicPoints
            ? ".location-item:not(.frozen):not([data-type='STRATEGICPOINT'])"
            : ".location-item:not(.frozen)",
          ghostClass: "sortable-ghost",
          chosenClass: "sortable-chosen",
          swapThreshold: 0.65,
          onStart: () => {
            isReordering.current = true;
          },
          onEnd: (evt) => {
            setTimeout(() => {
              handleReorder(evt);
            }, 0);
          },
        });
      }

      return () => {
        if (sortableRef.current) {
          sortableRef.current.destroy();
          sortableRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey, effectiveFreezeStrategicPoints]);

    /* ------------------------------ Assign truck/driver ------------------------------ */
    const handleTruckAssign = async (pickupDeliveryId, truckId, type, truckRole, truckPlate) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      const tUpper = String(type || "").toUpperCase();

      const manualPlate =
        truckId == null && typeof truckPlate === "string" && truckPlate.trim()
          ? truckPlate.trim().toUpperCase()
          : null;

      let optimisticPlate = manualPlate || null;

      // se veio id, tenta buscar plate real
      if (!optimisticPlate && truckId) {
        try {
          const tRes = await axios.get(`/trucks/${truckId}`, { validateStatus: () => true });
          optimisticPlate = tRes?.data?.plate ?? null;
        } catch {}
      }

      const prev = locationsRef.current;

      // optimistic UI
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (tUpper === "STRATEGICPOINT") {
            if (truckRole === "arrival") {
              return { ...loc, truckIdArrival: truckId ?? null, truckArrivalPlate: optimisticPlate };
            }
            if (truckRole === "departure") {
              return { ...loc, truckIdDeparture: truckId ?? null, truckDeparturePlate: optimisticPlate };
            }
            return loc;
          }

          return { ...loc, truckId: truckId ?? null, truckPlate: optimisticPlate };
        })
      );
      setResetKey((k) => k + 1);

      try {
        if (tUpper === "STRATEGICPOINT") {
          if (truckRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck-arrival`, {
              truckId: truckId ?? null,
              truckPlate: manualPlate, // ✅
            });
          } else if (truckRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck-departure`, {
              truckId: truckId ?? null,
              truckPlate: manualPlate, // ✅
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck`, {
            truckId: truckId ?? null,
            truckPlate: manualPlate, // ✅ (se o backend aceitar, ótimo; se ignorar, ok)
            type: String(type || "").toLowerCase(),
          });
        }

        setTimeout(() => softRefresh(), 100);
        if (refreshLoads) refreshLoads();
      } catch (e) {
        setLocations(prev);
        setResetKey((k) => k + 1);
        console.error("❌ Truck assignment failed:", e);
        alert(tl("alerts.assignTruckFailed", "Failed to assign truck. Please try again."));
      }
    };

    const handleRemoveTruck = async (pickupDeliveryId, truckRole) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      // ✅ If pickup/delivery belongs to EXTERNAL carrier -> clear manual plate on LOAD + UI
      const clickedLoad = clicked?.loadId ? (loads || []).find((l) => l.id === clicked.loadId) : null;

      const isPD = clicked?.type === "PICKUP" || clicked?.type === "DELIVERY";
      const isExternal = clickedLoad && isExternalTransportCarrier(clickedLoad);

      // ✅ default behavior (internal trucks + strategic arrival/departure)
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (truckRole === "arrival") return { ...loc, truckIdArrival: null, truckArrivalPlate: null };
          if (truckRole === "departure") return { ...loc, truckIdDeparture: null, truckDeparturePlate: null };

          return { ...loc, truckId: null, truckPlate: null };
        })
      );
      setResetKey((prevKey) => prevKey + 1);

      setIsLoading(true);
      try {
        if (truckRole === "arrival") {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck-arrival`, { 
            truckId: null,
            truckPlate: null,
          });
        } else if (truckRole === "departure") {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck-departure`, {
            truckId: null,
            truckPlate: null,
          });
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck`, {
            truckId: null,
            truckPlate: null,
            type: truckRole, // pickup | delivery
          });
        }

        fetchLocations();
        if (refreshLoads) refreshLoads();
      } catch (e) {
        alert(tl("alerts.unassignTruckFailed", "Failed to unassign truck."));
      }
      setIsLoading(false);
    };

    /* ------------------------------ Assign trailer ------------------------------ */
    const handleTrailerAssign = async (pickupDeliveryId, trailerId, type, trailerRole, trailerPlate) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      const prev = locationsRef.current;
      const tUpper = String(type || "").toUpperCase();

      const manualPlate =
        trailerId == null && typeof trailerPlate === "string" && trailerPlate.trim()
          ? trailerPlate.trim().toUpperCase()
          : null;

      const plateFromId = trailerId != null ? (trailersById[String(trailerId)] ?? null) : null;
      const optimisticPlate = manualPlate || plateFromId || null;

      // optimistic
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (tUpper === "STRATEGICPOINT") {
            if (trailerRole === "arrival") {
              return { ...loc, trailerIdArrival: trailerId ?? null, trailerArrivalPlate: optimisticPlate };
            }
            if (trailerRole === "departure") {
              return { ...loc, trailerIdDeparture: trailerId ?? null, trailerDeparturePlate: optimisticPlate };
            }
            return loc;
          }

          return {
            ...loc,
            trailerId: trailerId ?? null,
            trailerPlate: optimisticPlate ?? null,
          };
        })
      );
      setResetKey((k) => k + 1);

      try {
        if (tUpper === "STRATEGICPOINT") {
          if (trailerRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer-arrival`, {
              trailerId: trailerId ?? null,
              trailerPlate: manualPlate, // ✅
            });
          } else if (trailerRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer-departure`, {
              trailerId: trailerId ?? null,
              trailerPlate: manualPlate, // ✅
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer`, {
            trailerId: trailerId ?? null,
            trailerPlate: manualPlate, // ✅
          });
        }

        setTimeout(() => softRefresh(), 120);
        if (refreshLoads) refreshLoads();
      } catch (e) {
        console.error("❌ Trailer assignment failed:", e);
        setLocations(prev);
        setResetKey((k) => k + 1);
        alert(tl("alerts.assignTrailerFailed", "Failed to assign trailer. Please try again."));
      }
    };

    const handleRemoveTrailer = async (pickupDeliveryId, type, trailerRole) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      const prev = locationsRef.current;
      const tUpper = String(type || "").toUpperCase();

      // optimistic
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (tUpper === "STRATEGICPOINT") {
            if (trailerRole === "arrival") return { ...loc, trailerIdArrival: null, trailerArrivalPlate: null };
            if (trailerRole === "departure") return { ...loc, trailerIdDeparture: null, trailerDeparturePlate: null };
            return loc;
          }

          return { ...loc, trailerId: null, trailerPlate: null };
        })
      );
      setResetKey((k) => k + 1);

      try {
        if (tUpper === "STRATEGICPOINT") {
          if (trailerRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer-arrival`, {
              trailerId: null,
              trailerPlate: null, // ✅
            });
          } else if (trailerRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer-departure`, {
              trailerId: null,
              trailerPlate: null, // ✅
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer`, {
            trailerId: null,
            trailerPlate: null, // ✅
          });
        }

        setTimeout(() => softRefresh(), 120);
        if (refreshLoads) refreshLoads();
      } catch (e) {
        console.error("❌ Trailer unassignment failed:", e);
        setLocations(prev);
        setResetKey((k) => k + 1);
        alert(tl("alerts.unassignTrailerFailed", "Failed to unassign trailer."));
      }
    };

    const handleDriverAssign = async (
      pickupDeliveryId,
      driverId,
      driverName,
      type,
      driverRole,
      driverSlot = 1
    ) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      const prev = locationsRef.current;

      const tUpper = String(type || "").toUpperCase();
      const isStrategic = tUpper === "STRATEGICPOINT" || tUpper === "ARRIVAL" || tUpper === "DEPARTURE";

      const resolvedRole =
        driverRole ||
        (tUpper === "ARRIVAL" ? "arrival" : tUpper === "DEPARTURE" ? "departure" : undefined);

      // optimistic
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (isStrategic) {
            if (resolvedRole === "arrival") {
              return driverSlot === 2
                ? {
                    ...loc,
                    driver2IdArrival: driverId ?? null,
                    driver2NameArrival: driverName ?? null,
                    driverRole: "arrival",
                  }
                : {
                    ...loc,
                    driverIdArrival: driverId ?? null,
                    driverNameArrival: driverName ?? null,
                    driverRole: "arrival",
                  };
            }

            if (resolvedRole === "departure") {
              return driverSlot === 2
                ? {
                    ...loc,
                    driver2IdDeparture: driverId ?? null,
                    driver2NameDeparture: driverName ?? null,
                    driverRole: "departure",
                  }
                : {
                    ...loc,
                    driverIdDeparture: driverId ?? null,
                    driverNameDeparture: driverName ?? null,
                    driverRole: "departure",
                  };
            }

            return loc;
          }

          return driverSlot === 2
            ? { ...loc, driver2Id: driverId ?? null, driver2Name: driverName ?? null }
            : { ...loc, driverId: driverId ?? null, driverName: driverName ?? null };
        })
      );
      setResetKey((k) => k + 1);

      try {
        if (isStrategic) {
          if (resolvedRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver-arrival`, {
              driverId,
              driverSlot,
            });
          } else if (resolvedRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver-departure`, {
              driverId,
              driverSlot,
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver`, {
            driverId,
            type: String(type || "").toLowerCase(), // pickup | delivery
            driverSlot,
          });
        }

        setTimeout(() => softRefresh(), 100);
        if (refreshLoads) refreshLoads();
      } catch (e) {
        setLocations(prev);
        setResetKey((k) => k + 1);
        console.error("❌ Driver assignment failed:", e);
        alert(tl("alerts.assignDriverFailed", "Failed to assign driver. Please try again."));
      }
    };

    const handleRemoveDriver = async (pickupDeliveryId, driverRole, driverSlot = 1) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      // ✅ If pickup/delivery belongs to EXTERNAL carrier -> clear manual name on LOAD + UI
      const clickedLoad = clicked?.loadId
        ? (loads || []).find((l) => Number(l.id) === Number(clicked.loadId))
        : null;

      const isPD = clicked?.type === "PICKUP" || clicked?.type === "DELIVERY";
      const isExternal = clickedLoad && isExternalTransportCarrier(clickedLoad);

      if (isPD && isExternal && clicked?.loadId) {
        const loadId = Number(clicked.loadId);

        const clearedPayload =
          clicked.type === "DELIVERY" ? { driverDeliveryInfo: null } : { driverPickupInfo: null };

        // optimistic: clear driver for ALL same load + same type
        setLocations((prev) =>
          prev.map((loc) => {
            if (Number(loc.loadId) !== loadId) return loc;
            if (clicked.type === "DELIVERY" && loc.type !== "DELIVERY") return loc;
            if (clicked.type === "PICKUP" && loc.type !== "PICKUP") return loc;
            return driverSlot === 2
              ? { ...loc, driver2Id: null, driver2Name: null }
              : { ...loc, driverId: null, driverName: null };
          })
        );
        setResetKey((k) => k + 1);

        try {
          await axios.put(`/loads/${loadId}`, clearedPayload);
          setTimeout(() => softRefresh(), 120);
          if (refreshLoads) refreshLoads();
        } catch (e) {
          console.error("❌ Failed to clear free-text driver:", e);
          alert(tl("alerts.unassignDriverFailed", "Failed to unassign driver."));
          setTimeout(() => softRefresh(), 150);
        }
        return;
      }

      // ✅ default behavior (internal drivers + strategic arrival/departure)
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (driverRole === "arrival") {
            return driverSlot === 2
              ? { ...loc, driver2IdArrival: null, driver2NameArrival: null }
              : { ...loc, driverIdArrival: null, driverNameArrival: null };
          }

          if (driverRole === "departure") {
            return driverSlot === 2
              ? { ...loc, driver2IdDeparture: null, driver2NameDeparture: null }
              : { ...loc, driverIdDeparture: null, driverNameDeparture: null };
          }

          return driverSlot === 2
            ? { ...loc, driver2Id: null, driver2Name: null }
            : { ...loc, driverId: null, driverName: null };
        })
      );
      setResetKey((prevKey) => prevKey + 1);

      try {
          if (driverRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver-arrival`, {
              driverId: null,
              driverSlot,
            });
          } else if (driverRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver-departure`, {
              driverId: null,
              driverSlot,
            });
          } else {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver`, {
              driverId: null,
              type: driverRole, // pickup | delivery
              driverSlot,
            });
          }

        fetchLocations();
        if (refreshLoads) refreshLoads();
      } catch (e) {
        alert(tl("alerts.unassignDriverFailed", "Failed to unassign driver."));
      }
    };

    /* ------------------------------ Inventory/CMR flow ------------------------------ */
    const openInventoryForLastDelivery = async (deliveryPd) => {
      try {
        const loadId = deliveryPd.loadId;
        const currentLoad = (loads || []).find((l) => Number(l.id) === Number(loadId));
        
        // ⚠️ Don't block just because status is COMPLETED/CANCELLED.
        // Real finalization is /finalize-cmr (history+ws), not status.

        setSelectedLoadForCmr(loadId);

        // ✅ NEW: External/subcontracted carrier -> SKIP inventory and finalize immediately
        if (currentLoad && isExternalTransportCarrier(currentLoad)) {
          await finalizeAfterInventory(loadId);
          return;
        }

        const trailerId =
          currentLoad?.trailerIdDelivery ??
          currentLoad?.trailerIdPickup ??
          deliveryPd?.trailerId ??
          deliveryPd?.trailer?.id ??
          null;

        if (trailerId) {
          try {
            const tRes = await axios.get(`/trailers/${trailerId}`);
            const tr = tRes.data || {};
            setTrailerMeta({ id: tr.id, trailerPlate: tr.trailerPlate, status: tr.status });
            setTrailerInvData({
              numberOfStraps: tr.numberOfStraps ?? 0,
              numberOfRatchets: tr.numberOfRatchets ?? 0,
              numberOfEdgeProtectors: tr.numberOfEdgeProtectors ?? 0,
              numberOfAntiSlipMats: tr.numberOfAntiSlipMats ?? 0,
              numberOfWoodenBeams: tr.numberOfWoodenBeams ?? 0,
              numberOfSidebars: tr.numberOfSidebars ?? 0,
              numberOfFireExtinguishers: tr.numberOfFireExtinguishers ?? 0,
            });
          } catch (e) {
            console.warn("Could not fetch trailer; will skip trailer inventory.", e);
            setTrailerMeta(null);
            setTrailerInvData(null);
          }
        } else {
          setTrailerMeta(null);
          setTrailerInvData(null);
        }

        const truckId = deliveryPd?.truckId ?? null;

        // ✅ Only inventory prompts now (no CMR modal anymore)
        if (truckId) {
          const res = await axios.get(`/trucks/${truckId}`);
          const trk = res.data || {};
          setInventoryTruck({ id: trk.id, plate: trk.plate });
          setInventoryData({
            number_of_straps: trk.number_of_straps ?? 0,
            number_of_ratchets: trk.number_of_ratchets ?? 0,
            number_of_edge_protectors: trk.number_of_edge_protectors ?? 0,
            number_of_anti_slip_mats: trk.number_of_anti_slip_mats ?? 0,
          });
          setInventoryModalOpen(true);
        } else if (trailerId) {
          setTrailerInventoryModalOpen(true);
        } else {
          // ✅ nothing to prompt -> still must finalize (move to LoadHistory)
          await finalizeAfterInventory(loadId);
          return;
        }
      } catch (e) {
        console.error("Failed to prepare inventory flow:", e);
      }
    };

    const handleDeleteStrategicPoint = async (pickupDeliveryId) => {
      const ok = window.confirm(
        tl("confirm.deleteStrategicPoint", "Are you sure you want to delete this strategic point?")
      );
      if (!ok) return;

      try {
        await axios.delete(`/pickupdeliveries/${pickupDeliveryId}`);
        fetchLocations();
        if (refreshLoads) refreshLoads();
      } catch (err) {
        alert(tl("alerts.deleteStrategicPointFailed", "❌ Failed to delete strategic point."));
        console.error("Delete error:", err);
      }
    };

    const startLastDeliveryCmrFlow = async (deliveryPd, loadId) => {
      setPendingInventoryAfterCmr({
        deliveryPd,
        loadId: Number(loadId),
      });

      setSelectedLoadForCmr(Number(loadId));
      setSelectedPdForPhotos(deliveryPd?.id ?? null);
      setCmrLoadModalOpen(true);
    };

    const continuePendingInventoryAfterCmr = async () => {
      const pending = pendingInventoryAfterCmr;

      setCmrLoadModalOpen(false);
      setSelectedPdForPhotos(null);
      setPendingInventoryAfterCmr(null);

      if (!pending?.deliveryPd || !pending?.loadId) return;

      await openInventoryForLastDelivery(pending.deliveryPd);
    };

    // ✅ status cannot move off PENDING without truck + driver (also supports free-text for external carriers)
    const isPendingStatus = (s) => String(s || "").toUpperCase() === "PENDING";

    const hasTruckAssignedForPoint = (loc) => {
      const hasId = !!loc?.truckId;
      const hasPlate = !!String(loc?.truckPlate || "").trim();
      return hasId || hasPlate;
    };

    const hasDriverAssignedForPoint = (loc) => {
      const hasId = !!loc?.driverId;
      const hasName = !!String(loc?.driverName || "").trim();
      return hasId || hasName;
    };

    const canChangeStatusOffPending = (loc) => {
      if (!loc) return false;
      const t = String(loc.type || "").toUpperCase();
      if (t === "STRATEGICPOINT") return true;
      return hasTruckAssignedForPoint(loc) && hasDriverAssignedForPoint(loc);
    };

    const statusLabel = (status) => {
      const key = String(status || "").toUpperCase();
      if (!key) return "";

      return t(`status_selector_modal:statuses.${key}`, {
        defaultValue: t(`status_selector_modal:subOptions.${key}`, {
          defaultValue: t(`status_selector_modal:subSubOptions.${key}`, {
            defaultValue: t(`status_selector_modal:subSubSubOptions.${key}`, {
              defaultValue: tl(`statuses.${key}`, key),
            }),
          }),
        }),
      });
    };

    const typeLabel = (type) => {
      const key = String(type || "").toUpperCase();
      return tl(`types.${key}`, key);
    };

    const getBlockedStatusHoverMessage = () =>
      tl(
        "tooltips.blockedStatusNeedsNextPointReverted",
        'If you want to edit the status of this point, then you should revert the next point status from "Complete/Cancelled" to "Pendent".'
      );

    const isAssignmentLocked = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();
      return location?.isFrozen || CLOSED_STATUSES.includes(statusUpper);
    };

    const getBlockedAssignmentHoverMessage = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();

      if (location?.isFrozen) {
        return tl("tooltips.assignmentLockedFrozen", "Editing is locked");
      }

      if (statusUpper === "COMPLETED" || statusUpper === "CANCELLED") {
        return tl(
          "tooltips.assignmentLockedClosedPoint",
          'This point is already "{{status}}" — truck, trailer and driver can no longer be changed.',
          { status: statusLabel(statusUpper) }
        );
      }

      return "";
    };

    const isClosedPoint = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();
      return CLOSED_STATUSES.includes(statusUpper);
    };

    const isClosedStrategicPoint = (location) => {
      const typeUpper = String(location?.type || "").toUpperCase();
      return typeUpper === "STRATEGICPOINT" && isClosedPoint(location);
    };

    const getClosedStrategicPointAssignmentMessage = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();

      return tl(
        "alerts.cannotChangeAssignmentClosedStrategicPoint",
        '❌ Este ponto estratégico já está "{{status}}" e, por isso, não pode alterar o camião, reboque ou motorista.',
        { status: statusLabel(statusUpper) }
      );
    };

    const getClosedStrategicPointEditMessage = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();

      return tl(
        "alerts.cannotEditClosedStrategicPoint",
        '❌ Este ponto estratégico já está "{{status}}" e, por isso, não pode ser editado ou apagado.',
        { status: statusLabel(statusUpper) }
      );
    };

    const guardStrategicPointEditAction = (location) => {
      if (!location) return true;

      if (isClosedStrategicPoint(location)) {
        alert(getClosedStrategicPointEditMessage(location));
        return true;
      }

      return !!location?.isFrozen;
    };

    const isAssignmentBlockedNoAlert = (location) => {
      if (!location) return true;
      return isAssignmentLocked(location);
    };

    const guardAssignmentAction = (location) => {
      if (!location) return true;

      if (isClosedStrategicPoint(location)) {
        alert(getClosedStrategicPointAssignmentMessage(location));
        return true;
      }

      return isAssignmentLocked(location);
    };

    const getAssignmentButtonDisabled = (location) => {
      if (!location) return true;

      // Para pontos estratégicos concluídos/cancelados, NÃO usamos disabled,
      // senão o clique não chega ao onClick e não conseguimos mostrar o aviso.
      if (isClosedStrategicPoint(location)) {
        return !!isLoading || !!location?.isFrozen;
      }

      return !!isLoading || isAssignmentLocked(location);
    };

    const hasAssignedTruck = (location, role = null) => {
      const typeUpper = String(location?.type || "").toUpperCase();

      if (typeUpper === "STRATEGICPOINT") {
        if (role === "arrival") {
          return !!(location?.truckIdArrival || String(location?.truckArrivalPlate || "").trim());
        }
        if (role === "departure") {
          return !!(location?.truckIdDeparture || String(location?.truckDeparturePlate || "").trim());
        }
        return false;
      }

      return !!(location?.truckId || String(location?.truckPlate || "").trim());
    };

    const hasAssignedTrailer = (location, role = null) => {
      const typeUpper = String(location?.type || "").toUpperCase();

      if (typeUpper === "STRATEGICPOINT") {
        if (role === "arrival") {
          return !!(location?.trailerIdArrival || String(location?.trailerArrivalPlate || "").trim());
        }
        if (role === "departure") {
          return !!(location?.trailerIdDeparture || String(location?.trailerDeparturePlate || "").trim());
        }
        return false;
      }

      return !!(location?.trailerId || String(location?.trailerPlate || "").trim());
    };

    const hasAssignedDriver = (location, role = null, driverSlot = 1) => {
      const typeUpper = String(location?.type || "").toUpperCase();

      if (typeUpper === "STRATEGICPOINT") {
        if (role === "arrival") {
          return driverSlot === 2
            ? !!(location?.driver2IdArrival || String(location?.driver2NameArrival || "").trim())
            : !!(location?.driverIdArrival || String(location?.driverNameArrival || "").trim());
        }

        if (role === "departure") {
          return driverSlot === 2
            ? !!(location?.driver2IdDeparture || String(location?.driver2NameDeparture || "").trim())
            : !!(location?.driverIdDeparture || String(location?.driverNameDeparture || "").trim());
        }

        return false;
      }

      return driverSlot === 2
        ? !!(location?.driver2Id || String(location?.driver2Name || "").trim())
        : !!(location?.driverId || String(location?.driverName || "").trim());
    };

    return (
      <>
        <ModalPortal>
          <DateConflictModal
            visible={showModal}
            minDate={modalData?.prevDate ? new Date(modalData.prevDate) : null}
            maxDate={modalData?.nextDate ? new Date(modalData.nextDate) : null}
            minHour={modalData?.prevHour}
            maxHour={modalData?.nextHour}
            onConfirm={handleDateModalConfirm}
            onCancel={handleCancelModal}
            existingDateHours={existingDateHours}
          />

          {/* Free-text truck modal (external carriers) */}
          {freeTextTruckModalOpen && freeTextTruckCtx?.loadId && (
            <FreeTextTruckModal
            isOpen={freeTextTruckModalOpen}
            onClose={() => {
              setFreeTextTruckModalOpen(false);
              setFreeTextTruckCtx(null);
            }}
            title={
              freeTextTruckCtx.type === "DELIVERY"
                ? tl("modalTitles.freeTextTruckDelivery", "Set Delivery Truck — Load #{{id}}", {
                    id: freeTextTruckCtx.loadId,
                  })
                : tl("modalTitles.freeTextTruckPickup", "Set Pick-up Truck — Load #{{id}}", {
                    id: freeTextTruckCtx.loadId,
                  })
            }
            initialValue={""}
            maxLen={15}
            lettersOnly={true}
            onSubmit={async (value) => {
              const loadId = Number(freeTextTruckCtx.loadId);
              const cleaned = String(value || "").trim().slice(0, 15);

              const payload =
                freeTextTruckCtx.type === "DELIVERY"
                  ? { truckDeliveryInfo: `${cleaned} • manual` }
                  : { truckPickupInfo: `${cleaned} • manual` };

              // Optimistic: apply to ALL points of that load + type
              setLocations((prev) =>
                prev.map((loc) => {
                  if (loc.loadId !== loadId) return loc;
                  if (freeTextTruckCtx.type === "DELIVERY" && loc.type !== "DELIVERY") return loc;
                  if (freeTextTruckCtx.type === "PICKUP" && loc.type !== "PICKUP") return loc;
                  return { ...loc, truckId: null, truckPlate: cleaned };
                })
              );
              setResetKey((k) => k + 1);

              try {
                await axios.put(`/loads/${loadId}`, payload);
                setTimeout(() => softRefresh(), 120);
                if (refreshLoads) refreshLoads();
              } catch (e) {
                console.error("❌ Free-text truck save failed:", e);
                alert(tl("alerts.assignTruckFailed", "Failed to assign truck. Please try again."));
                setTimeout(() => softRefresh(), 150);
              } finally {
                setFreeTextTruckModalOpen(false);
                setFreeTextTruckCtx(null);
              }
            }}
          />
        )}

        {/* Free-text driver modal (external carriers) */}
        {freeTextDriverModalOpen && freeTextDriverCtx?.loadId && (
          <FreeTextDriverModal
            isOpen={freeTextDriverModalOpen}
            onClose={() => {
              setFreeTextDriverModalOpen(false);
              setFreeTextDriverCtx(null);
            }}
            title={
              freeTextDriverCtx.type === "DELIVERY"
                ? tl("modalTitles.freeTextDriverDelivery", "Set Delivery Driver — Load #{{id}}", {
                    id: freeTextDriverCtx.loadId,
                  })
                : tl("modalTitles.freeTextDriverPickup", "Set Pick-up Driver — Load #{{id}}", {
                    id: freeTextDriverCtx.loadId,
                  })
            }
            initialValue={""}
            maxLen={40}
            lettersOnly={true}
            placeholder={tl("placeholders.driverName", "e.g. João Silva")}
            onSubmit={async (value) => {
              const loadId = Number(freeTextDriverCtx.loadId);
              const cleaned = String(value || "").trim().slice(0, 40);
              if (!cleaned) return;

              const payload =
                freeTextDriverCtx.type === "DELIVERY"
                  ? { driverDeliveryInfo: `${cleaned} • manual` }
                  : { driverPickupInfo: `${cleaned} • manual` };

              // Optimistic: apply to ALL points of that load + type
              setLocations((prev) =>
                prev.map((loc) => {
                  if (Number(loc.loadId) !== loadId) return loc;
                  if (freeTextDriverCtx.type === "DELIVERY" && loc.type !== "DELIVERY") return loc;
                  if (freeTextDriverCtx.type === "PICKUP" && loc.type !== "PICKUP") return loc;
                  return { ...loc, driverId: null, driverName: cleaned };
                })
              );
              setResetKey((k) => k + 1);

              try {
                await axios.put(`/loads/${loadId}`, payload);
                setTimeout(() => softRefresh(), 120);
                if (refreshLoads) refreshLoads();
              } catch (e) {
                console.error("❌ Free-text driver save failed:", e);
                alert(tl("alerts.assignDriverFailed", "Failed to assign driver. Please try again."));
                setTimeout(() => softRefresh(), 150);
              } finally {
                setFreeTextDriverModalOpen(false);
                setFreeTextDriverCtx(null);
              }
            }}
          />
        )}

        {truckModalOpen && selectedPD && (
          <TruckSelectorModal
            isOpen={truckModalOpen}
            onClose={() => setTruckModalOpen(false)}
            onSelect={(truck, selectedType, alsoAssignToDeparture) => {
              const tid = truck?.id ?? null;

              // ✅ aceita manual selection (quickMode)
              const manualPlate =
                truck?.plate ??
                truck?.truckPlate ??
                truck?.manualPlate ??
                null;

              if (!tid && !manualPlate) return;

              const apply = (role) => {
                handleTruckAssign(
                  selectedPD.id,
                  tid,
                  selectedPD.type,          // mantém "STRATEGICPOINT" quando for
                  role,
                  manualPlate               // ✅ novo
                );
              };

              if (
                alsoAssignToDeparture &&
                selectedPD?.type === "STRATEGICPOINT" &&
                !isFuelStrategicPoint(selectedPD)
              ) {
                apply("arrival");
                apply("departure");
              } else {
                apply(selectedPD.truckRole || "arrival");
              }

              setTruckModalOpen(false);
              setSelectedPD(null);
            }}
            selectedType={selectedPD.type}
            selectedPickupDelivery={selectedPD}
            currentLoad={(() => {
              const l =
                selectedPD.load ||
                loads.find((x) => Number(x.id) === Number(selectedPD.loadId)) ||
                null;

              if (!l) return null;

              return { ...l, isExternalCarrier: isExternalTransportCarrier(l) };
            })()}
            title={
              selectedPD.type === "STRATEGICPOINT"
                ? tl(
                    "modalTitles.assignTruckStrategicPoint",
                    "Assign truck to strategic point"
                  )
                : tl(
                    "modalTitles.assignTruckToPoint",
                    "Assign truck to {{type}} #{{id}}",
                    { type: typeLabel(selectedPD.type), id: selectedPD.id }
                  )
            }
            allPickups={selectedPD.type === "STRATEGICPOINT" ? [] : getAllPickups(loads, selectedPD.loadId)}
            allDeliveries={selectedPD.type === "STRATEGICPOINT" ? [] : getAllDeliveries(loads, selectedPD.loadId)}
          />
        )}

        {trailerModalOpen && selectedPDForTrailer && (
          <TrailerSelectorModal
            isOpen={trailerModalOpen}
            onClose={() => {
              setTrailerModalOpen(false);
              setSelectedPDForTrailer(null);
            }}
            onSelect={(trailer) => {
              // ✅ aceita tanto trailer interno (id) como manual (plate)
              const tid = trailer?.id ?? null;
              const plate = trailer?.trailerPlate ?? trailer?.trailerNumber ?? trailer?.plate ?? trailer?.manualPlate ?? null;

              if (!tid && !plate) return;

              if (
                String(selectedPDForTrailer?.type || "").toUpperCase() === "STRATEGICPOINT" &&
                selectedPDForTrailer?.trailerRole === "both"
              ) {
                handleTrailerAssign(
                  selectedPDForTrailer.id,
                  tid,
                  selectedPDForTrailer.type,
                  "arrival",
                  plate
                );

                handleTrailerAssign(
                  selectedPDForTrailer.id,
                  tid,
                  selectedPDForTrailer.type,
                  "departure",
                  plate
                );
              } else {
                handleTrailerAssign(
                  selectedPDForTrailer.id,
                  tid,
                  selectedPDForTrailer.type,
                  selectedPDForTrailer.trailerRole,
                  plate
                );
              }

              setTrailerModalOpen(false);
              setSelectedPDForTrailer(null);
            }}
            selectedPickupDelivery={selectedPDForTrailer}
            currentLoad={(() => {
              const l =
                loads.find((x) => Number(x.id) === Number(selectedPDForTrailer.loadId)) ||
                selectedPDForTrailer.load ||
                null;

              if (!l) return null;

              // ensure quickMode works
              return { ...l, isExternalCarrier: isExternalTransportCarrier(l) };
            })()}
            title={tl("modalTitles.assignTrailerToPoint", "Assign trailer")}
            selectedType={
              String(selectedPDForTrailer?.type || "").toUpperCase() === "DELIVERY"
                ? "delivery"
                : "pickup"
            }
            mode="single"
          />
        )}

        {driverModalOpen && selectedPDForDriver && (
          <DriverSelectorModal
            isOpen={driverModalOpen}
            driverSlot={selectedDriverSlot}
            onClose={() => setDriverModalOpen(false)}
            setIsDriverUpdating={() => {}}
            onSelect={(driver, selectedType, alsoAssignToDeparture, driverSlotFromModal = selectedDriverSlot) => {
              if (!driver?.id) return;

              const name = getDriverDisplayName(driver);
              const slot = Number(driverSlotFromModal) === 2 ? 2 : 1;

              if (
                alsoAssignToDeparture &&
                selectedPDForDriver?.type === "STRATEGICPOINT" &&
                !isFuelStrategicPoint(selectedPDForDriver)
              ) {
                if (selectedPDForDriver.driverRole === "arrival") {
                  handleDriverAssign(selectedPDForDriver.id, driver.id, name, "STRATEGICPOINT", "arrival", slot);
                  handleDriverAssign(selectedPDForDriver.id, driver.id, name, "STRATEGICPOINT", "departure", slot);
                } else if (selectedPDForDriver.driverRole === "departure") {
                  handleDriverAssign(selectedPDForDriver.id, driver.id, name, "STRATEGICPOINT", "departure", slot);
                  handleDriverAssign(selectedPDForDriver.id, driver.id, name, "STRATEGICPOINT", "arrival", slot);
                }
              } else {
                handleDriverAssign(
                  selectedPDForDriver.id,
                  driver.id,
                  name,
                  selectedPDForDriver.type,
                  selectedPDForDriver.driverRole,
                  slot
                );
              }

              setDriverModalOpen(false);
              setSelectedPDForDriver(null);
            }}
            selectedType={selectedPDForDriver.type}
            selectedPickupDelivery={selectedPDForDriver}
            currentLoad={(() => {
              const l =
                selectedPDForDriver.load ||
                loads.find((x) => Number(x.id) === Number(selectedPDForDriver.loadId)) ||
                null;

              if (!l) return null;

              return { ...l, isExternalCarrier: isExternalTransportCarrier(l) };
            })()}
            title={
              selectedPDForDriver.type === "STRATEGICPOINT"
                ? tl(
                    "modalTitles.assignDriverStrategicPoint",
                    "Assign driver to strategic point"
                  )
                : tl(
                    "modalTitles.assignDriverToPoint",
                    "Assign driver to {{type}} #{{id}}",
                    { type: typeLabel(selectedPDForDriver.type), id: selectedPDForDriver.id }
                  )
            }
            allPickups={
              selectedPDForDriver.type ===
              "STRATEGICPOINT"
                ? []
                : locations.filter(
                    (
                      point
                    ) =>
                      Number(
                        point?.loadId
                      ) ===
                        Number(
                          selectedPDForDriver
                            .loadId
                        ) &&
                      String(
                        point?.type ||
                          ""
                      ).toUpperCase() ===
                        "PICKUP"
                  )
            }
            allDeliveries={
              selectedPDForDriver.type ===
              "STRATEGICPOINT"
                ? []
                : locations.filter(
                    (
                      point
                    ) =>
                      Number(
                        point?.loadId
                      ) ===
                        Number(
                          selectedPDForDriver
                            .loadId
                        ) &&
                      String(
                        point?.type ||
                          ""
                      ).toUpperCase() ===
                        "DELIVERY"
                  )
            }
          />
        )}

        <StrategicPointModal
          isOpen={strategicModalOpen}
          onClose={() => setStrategicModalOpen(false)}
          onSubmit={async (data) => {
            try {
              await axios.post("/pickupdeliveries", {
                ...data,
                strategicPointPurpose:
                  String(data?.strategicPointPurpose || "GENERAL").toUpperCase() === "FUEL"
                    ? "FUEL"
                    : "GENERAL",
                hasTrailerSwap: !!data?.hasTrailerSwap,
                transportsessionId,
                type: "STRATEGICPOINT",
              });

              setStrategicModalOpen(false);
              fetchLocations();
              if (refreshLoads) refreshLoads();
            } catch (err) {
              alert(tl("alerts.addStrategicPointFailed", "Failed to add strategic point."));
            }
          }}
        />

        <EditStrategicPointModal
          isOpen={editStrategicModalOpen}
          onClose={() => {
            setEditStrategicModalOpen(false);
            setSelectedStrategicPointToEdit(null);
          }}
          strategicPoint={selectedStrategicPointToEdit}
          transportsessionId={transportsessionId}
          existingDateHours={existingDateHours}
          onSaved={() => {
            fetchLocations();
            if (refreshLoads) refreshLoads();
          }}
        />

        {mapModalOpen && (
          <TransportSessionMapModal
            isOpen={mapModalOpen}
            onClose={() => setMapModalOpen(false)}
            transportsessionId={transportsessionId}
            points={locationsWithLock}
            onReorderPoints={handleMapReorder}
          />
        )}
        </ModalPortal>

        <div
          className={[
            "transport-session-motion",
            sessionMotionReady && !isClosingSession ? "is-open" : "",
            isClosingSession ? "is-closing" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="transport-session-motion-inner">
            <div className="transport-session-actions-row">
              <button
                type="button"
                className="transport-session-map-btn"
                disabled={isLoading}
                onClick={() => setMapModalOpen(true)}
                title={tl("tooltips.viewMap", "View map")}
              >
                <FaMapMarkerAlt />
                {tl("buttons.viewMap", "Ver mapa")}
              </button>

              <button
                className="add-strategic-point-btn"
                style={{
                  padding: "8px 15px",
                  background: "#0079A7",
                  color: "white",
                  borderRadius: "8px",
                  fontWeight: 400,
                  fontSize: 12,
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.10)",
                }}
                disabled={isLoading || effectiveFreezeStrategicPoints}
                onClick={() => setStrategicModalOpen(true)}
                title={tl("tooltips.addStrategicPoint", "Add strategic point")}
              >
                {tl("buttons.addStrategicPoint", "+ ADD STRATEGIC POINT")}
              </button>
            </div>

            <div
              ref={listRef}
              className={`location-timeline ${isClosingSession ? "is-closing" : ""}`}
              data-sortable-reset={resetKey}
            >
              {locationsWithLock.map((location, index) => (
                <React.Fragment key={`${location.type}-${location.id}`}>
              <div
                className={`location-item ${location.isLocked ? "status-locked" : ""} ${
                  location.isFrozen ? "frozen" : ""
                }`}
                data-id={location.id}
                data-type={location.type}
                data-location={location.location}
                data-date={location.date || ""}
                data-hour={location.hour || "00:00"}
                data-loadid={location.loadId}
              >
                <div className="drag-handle">
                  <FaGripLines
                    size={18}
                    style={{
                      cursor: (location.isFrozen || CLOSED_STATUSES.includes(String(location.status || "").toUpperCase())) ? "not-allowed" : "grab",
                      opacity: (location.isFrozen || CLOSED_STATUSES.includes(String(location.status || "").toUpperCase())) ? 0.4 : 1,
                    }}
                    title={
                      (location.isFrozen || CLOSED_STATUSES.includes(String(location.status || "").toUpperCase()))
                        ? tl("tooltips.statusLocked", "Editing is locked")
                        : tl("tooltips.dragToReorder", "Drag to reorder")
                    }
                  />
                </div>

                <div className="icon-container">
                  {location.type === "PICKUP" ? (
                    <div className="pickup-icon" />
                  ) : location.type === "DELIVERY" ? (
                    <FaMapMarkerAlt className="delivery-icon" />
                  ) : location.type === "STRATEGICPOINT" ? (
                    isFuelStrategicPoint(location) ? (
                      <FaGasPump className="strategic-point-icon fuel-strategic-point-icon" />
                    ) : (
                      <FaBullseye className="strategic-point-icon" />
                    )
                  ) : null}
                </div>

                <div className="location-info">
                  <div className="location-header">
                    <span className="location-label">
                      {location.type === "PICKUP"
                        ? tl("labels.pickupPlace", "PICK-UP PLACE")
                        : location.type === "DELIVERY"
                        ? tl("labels.deliveryPlace", "DELIVERY PLACE")
                        : location.type === "STRATEGICPOINT"
                        ? isFuelStrategicPoint(location)
                          ? tl("labels.fuelStrategicPoint", "FUEL STOP")
                          : tl("labels.strategicPoint", "STRATEGIC POINT")
                        : tl("labels.location", "LOCATION")}
                    </span>

                    <span className="location-name">({location.location})</span>

                    {location.date && (
                      <div className="location-date-hour">
                        <span className="location-date">{formatDateLocal(location.date)}</span>
                        {location.hour && <span className="location-hour">{location.hour}</span>}
                      </div>
                    )}

                    {location.type !== "STRATEGICPOINT" ? (
                      <span className="load-id">
                        {tl("labels.loadId", "LOAD ID: {{loadId}}", { loadId: location.loadId })}
                      </span>
                    ) : (
                      <span className="load-id load-id-placeholder">&nbsp;</span>
                    )}
                  </div>

                  {location.type === "STRATEGICPOINT" ? (
                    !hasTrailerSwapEnabled(location) ? (
                      <>
                        {/* Strategic point without trailer swap: single TRUCK / DRIVER / TRAILER */}
                        <div className="strategic-inline-assignments">
                          <div className="strategic-row">
                          <div className="assign-truck-btn-wrapper strategic-arrival strategic-inline-truck">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.fuelTruck", "Truck:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedTruck(location, "arrival") ? "assigned-truck-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTruckAssignUI(location, "arrival");
                                }}
                                title={
                                  isAssignmentBlockedNoAlert(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignTruck", "Assign Truck")
                                }
                              >
                                <Truck className="assign-truck-icon" size={16} />
                                <span className="assign-truck-label">
                                  {location.truckArrivalPlate
                                    ? location.truckArrivalPlate
                                    : location.truckIdArrival
                                    ? `#${location.truckIdArrival}`
                                    : tl("buttons.assignFuelTruck", "Assign Truck")}
                                </span>
                              </button>

                              {(location.truckIdArrival || location.truckArrivalPlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentBlockedNoAlert(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTruck", "Remove truck")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveTruck(location.id, "arrival");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="assign-truck-btn-wrapper strategic-arrival strategic-inline-drivers">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.fuelDriver", "Driver:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedDriver(location, "arrival", 1) ? "assigned-driver-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openDriverAssignUI(location, "arrival", 1);
                                }}
                                title={
                                  isAssignmentBlockedNoAlert(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignFuelDriver1", "Assign Driver 1")
                                }
                              >
                                <User className="assign-driver-icon" size={16} />
                                <span className="assign-driver-label">
                                  {location.driverNameArrival
                                    ? location.driverNameArrival
                                    : location.driverIdArrival
                                    ? tl("labels.driverAssigned", "Driver assigned")
                                    : tl("buttons.assignFuelDriver1", "Assign Driver 1")}
                                </span>
                              </button>

                              {(location.driverIdArrival || location.driverNameArrival) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeDriver1", "Remove driver 1")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveDriver(location.id, "arrival", 1);
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedDriver(location, "arrival", 2) ? "assigned-driver-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openDriverAssignUI(location, "arrival", 2);
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignFuelDriver2", "Assign Driver 2")
                                }
                              >
                                <User className="assign-driver-icon" size={16} />
                                <span className="assign-driver-label">
                                  {location.driver2NameArrival
                                    ? location.driver2NameArrival
                                    : location.driver2IdArrival
                                    ? tl("labels.driverAssigned", "Driver assigned")
                                    : tl("buttons.assignFuelDriver2", "Assign Driver 2")}
                                </span>
                              </button>

                              {(location.driver2IdArrival || location.driver2NameArrival) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeDriver2", "Remove driver 2")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveDriver(location.id, "arrival", 2);
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="strategic-row">
                          <div className="assign-truck-btn-wrapper strategic-arrival strategic-inline-trailer">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.fuelTrailerOptional", "Trailer (optional):")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedTrailer(location, "arrival") ? "assigned-trailer-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTrailerAssignUI(location, "arrival");
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignFuelTrailerOptional", "Assign Trailer (optional)")
                                }
                              >
                                <Container className="assign-truck-icon trailer-icon" size={16} />
                                <span className="assign-truck-label">
                                  {location.trailerArrivalPlate
                                    ? location.trailerArrivalPlate
                                    : location.trailerIdArrival
                                    ? (trailersById[String(location.trailerIdArrival)] || `#${location.trailerIdArrival}`)
                                    : tl("buttons.assignFuelTrailerOptional", "Assign Trailer (optional)")}
                                </span>
                              </button>

                              {(location.trailerIdArrival || location.trailerArrivalPlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTrailer", "Remove trailer")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "arrival");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        </div>
                      </>
                    ) : (
                      <>
                      {/* Arrival / Departure TRUCK */}
                      <div className="strategic-row">
                        <div className="assign-truck-btn-wrapper strategic-arrival">
                          <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                            {tl("labels.arrivalTruck", "Arrival Truck:")}
                          </span>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedTruck(location, "arrival") ? "assigned-truck-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openTruckAssignUI(location, "arrival");
                              }}
                              title={
                                isAssignmentBlockedNoAlert(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignTruck", "Assign Truck")
                              }
                            >
                              <Truck className="assign-truck-icon" size={16} />
                              <span className="assign-truck-label">
                                {location.truckArrivalPlate
                                  ? location.truckArrivalPlate
                                  : location.truckIdArrival
                                  ? `#${location.truckIdArrival}`
                                  : tl("buttons.assignTruck", "Assign Truck")}
                              </span>
                            </button>

                            {(location.truckIdArrival || location.truckArrivalPlate) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeTruck", "Remove truck")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveTruck(location.id, "arrival");
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="assign-truck-btn-wrapper strategic-departure">
                          <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                            {tl("labels.departureTruck", "Departure Truck:")}
                          </span>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedTruck(location, "departure") ? "assigned-truck-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openTruckAssignUI(location, "departure");
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignDepartureTruck", "Assign Departure Truck")
                              }
                            >
                              <Truck className="assign-truck-icon" size={16} />
                              <span className="assign-truck-label">
                                {location.truckDeparturePlate
                                  ? location.truckDeparturePlate
                                  : location.truckIdDeparture
                                  ? `#${location.truckIdDeparture}`
                                  : tl("buttons.assignDepartureTruck", "Assign Departure Truck")}
                              </span>
                            </button>

                            {(location.truckIdDeparture || location.truckDeparturePlate) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeTruck", "Remove truck")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveTruck(location.id, "departure");
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* TRAILER — 1 field by default, 2 fields when trailer swap is enabled */}
                      {hasTrailerSwapEnabled(location) ? (
                        <div className="strategic-row">
                          <div className="assign-truck-btn-wrapper strategic-arrival">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.arrivalTrailer", "Arrival Trailer:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedTrailer(location, "arrival") ? "assigned-trailer-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTrailerAssignUI(location, "arrival");
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignTrailer", "Assign Trailer")
                                }
                              >
                                <Container className="assign-truck-icon trailer-icon" size={16} />
                                <span className="assign-truck-label">
                                  {location.trailerArrivalPlate
                                    ? location.trailerArrivalPlate
                                    : location.trailerIdArrival
                                    ? (trailersById[String(location.trailerIdArrival)] || `#${location.trailerIdArrival}`)
                                    : tl("buttons.assignTrailer", "Assign Trailer")}
                                </span>
                              </button>

                              {(location.trailerIdArrival || location.trailerArrivalPlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTrailer", "Remove trailer")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "arrival");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="assign-truck-btn-wrapper strategic-departure">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.departureTrailer", "Departure Trailer:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedTrailer(location, "departure") ? "assigned-trailer-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTrailerAssignUI(location, "departure");
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignDepartureTrailer", "Assign Departure Trailer")
                                }
                              >
                                <Container className="assign-truck-icon trailer-icon" size={16} />
                                <span className="assign-truck-label">
                                  {location.trailerDeparturePlate
                                    ? location.trailerDeparturePlate
                                    : location.trailerIdDeparture
                                    ? (trailersById[String(location.trailerIdDeparture)] || `#${location.trailerIdDeparture}`)
                                    : tl("buttons.assignDepartureTrailer", "Assign Departure Trailer")}
                                </span>
                              </button>

                              {(location.trailerIdDeparture || location.trailerDeparturePlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTrailer", "Remove trailer")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "departure");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="strategic-row strategic-row-single">
                          <div className="assign-truck-btn-wrapper strategic-single-trailer">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.trailer", "Trailer:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${
                                  hasAssignedTrailer(location, "arrival") || hasAssignedTrailer(location, "departure")
                                    ? "assigned-trailer-btn"
                                    : ""
                                }`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTrailerAssignUI(location, "both");
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignTrailer", "Assign Trailer")
                                }
                              >
                                <Container className="assign-truck-icon trailer-icon" size={16} />
                                <span className="assign-truck-label">
                                  {location.trailerArrivalPlate
                                    ? location.trailerArrivalPlate
                                    : location.trailerDeparturePlate
                                    ? location.trailerDeparturePlate
                                    : location.trailerIdArrival
                                    ? (trailersById[String(location.trailerIdArrival)] || `#${location.trailerIdArrival}`)
                                    : location.trailerIdDeparture
                                    ? (trailersById[String(location.trailerIdDeparture)] || `#${location.trailerIdDeparture}`)
                                    : tl("buttons.assignTrailer", "Assign Trailer")}
                                </span>
                              </button>

                              {(location.trailerIdArrival ||
                                location.trailerArrivalPlate ||
                                location.trailerIdDeparture ||
                                location.trailerDeparturePlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTrailer", "Remove trailer")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;

                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "arrival");
                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "departure");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Arrival / Departure DRIVER */}
                      <div className="strategic-row">
                        <div className="assign-truck-btn-wrapper strategic-arrival">
                          <span className="truck-role-label strategic-arrival-driver-label" style={{ display: "block", marginBottom: 2 }}>
                            {tl("labels.arrivalDriver", "Arrival Driver:")}
                          </span>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedDriver(location, "arrival", 1) ? "assigned-driver-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openDriverAssignUI(location, "arrival", 1);
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignArrivalDriver1", "Assign Arrival Driver 1")
                              }
                            >
                              <User className="assign-driver-icon" size={16} />
                              <span className="assign-driver-label">
                                {location.driverNameArrival
                                  ? location.driverNameArrival
                                  : location.driverIdArrival
                                  ? tl("labels.driverAssigned", "Driver assigned")
                                  : tl("buttons.assignArrivalDriver1", "Assign Arrival Driver 1")}
                              </span>
                            </button>

                            {(location.driverIdArrival || location.driverNameArrival) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentBlockedNoAlert(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeArrivalDriver1", "Remove arrival driver 1")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveDriver(location.id, "arrival", 1);
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedDriver(location, "arrival", 2) ? "assigned-driver-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openDriverAssignUI(location, "arrival", 2);
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignArrivalDriver2", "Assign Arrival Driver 2")
                              }
                            >
                              <User className="assign-driver-icon" size={16} />
                              <span className="assign-driver-label">
                                {location.driver2NameArrival
                                  ? location.driver2NameArrival
                                  : location.driver2IdArrival
                                  ? tl("labels.driverAssigned", "Driver assigned")
                                  : tl("buttons.assignArrivalDriver2", "Assign Arrival Driver 2")}
                              </span>
                            </button>

                            {(location.driver2IdArrival || location.driver2NameArrival) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeArrivalDriver2", "Remove arrival driver 2")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveDriver(location.id, "arrival", 2);
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="assign-truck-btn-wrapper strategic-departure">
                          <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                            {tl("labels.departureDriver", "Departure Driver:")}
                          </span>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedDriver(location, "departure", 1) ? "assigned-driver-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openDriverAssignUI(location, "departure", 1);
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignDepartureDriver1", "Assign Departure Driver 1")
                              }
                            >
                              <User className="assign-driver-icon" size={16} />
                              <span className="assign-driver-label">
                                {location.driverNameDeparture
                                  ? location.driverNameDeparture
                                  : location.driverIdDeparture
                                  ? tl("labels.driverAssigned", "Driver assigned")
                                  : tl("buttons.assignDepartureDriver1", "Assign Departure Driver 1")}
                              </span>
                            </button>

                            {(location.driverIdDeparture || location.driverNameDeparture) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeDepartureDriver1", "Remove departure driver 1")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveDriver(location.id, "departure", 1);
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedDriver(location, "departure", 2) ? "assigned-driver-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openDriverAssignUI(location, "departure", 2);
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignDepartureDriver2", "Assign Departure Driver 2")
                              }
                            >
                              <User className="assign-driver-icon" size={16} />
                              <span className="assign-driver-label">
                                {location.driver2NameDeparture
                                  ? location.driver2NameDeparture
                                  : location.driver2IdDeparture
                                  ? tl("labels.driverAssigned", "Driver assigned")
                                  : tl("buttons.assignDepartureDriver2", "Assign Departure Driver 2")}
                              </span>
                            </button>

                            {(location.driver2IdDeparture || location.driver2NameDeparture) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeDepartureDriver2", "Remove departure driver 2")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveDriver(location.id, "departure", 2);
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )
                ) : (
                    <>
                      {/* Truck for PICKUP/DELIVERY */}
                      <div className="assign-truck-btn-wrapper">
                        <button
                          className={`assign-truck-btn ${hasAssignedTruck(location) ? "assigned-truck-btn" : ""}`}
                          disabled={getAssignmentButtonDisabled(location)}
                          onClick={() => {
                            if (guardAssignmentAction(location)) return;
                            openTruckAssignUI(location, location.type.toLowerCase()); // pickup | delivery
                          }}
                          style={{
                            cursor: isAssignmentBlockedNoAlert(location) ? "not-allowed" : "pointer",
                          }}
                          title={
                            isAssignmentBlockedNoAlert(location)
                              ? getBlockedAssignmentHoverMessage(location)
                              : tl("buttons.assignTruck", "Assign Truck")
                          }
                        >
                          <Truck className="assign-truck-icon" size={16} />
                          <span className="assign-truck-label">
                            {location.truckPlate
                              ? location.truckPlate
                              : location.truckId
                              ? `#${location.truckId}`
                              : tl("buttons.assignTruck", "Assign Truck")}
                          </span>
                        </button>

                        {(location.truckPlate || location.truckId) && (
                          <button
                            className="remove-truck-btn"
                            title={
                              isAssignmentBlockedNoAlert(location)
                                ? getBlockedAssignmentHoverMessage(location)
                                : tl("tooltips.removeTruck", "Remove truck")
                            }
                            disabled={getAssignmentButtonDisabled(location)}
                            onClick={() => {
                              if (guardAssignmentAction(location)) return;
                              handleRemoveTruck(location.id, location.type.toLowerCase());
                            }}
                            tabIndex={-1}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>

                      {/* Trailer for PICKUP/DELIVERY */}
                      <div className="assign-truck-btn-wrapper">
                        <button
                          className={`assign-truck-btn ${hasAssignedTrailer(location) ? "assigned-trailer-btn" : ""}`}
                          disabled={getAssignmentButtonDisabled(location)}
                          onClick={() => {
                            if (guardAssignmentAction(location)) return;
                            openTrailerAssignUI(location);
                          }}
                          style={{
                            cursor: isAssignmentBlockedNoAlert(location) ? "not-allowed" : "pointer",
                          }}
                          title={
                            isAssignmentBlockedNoAlert(location)
                              ? getBlockedAssignmentHoverMessage(location)
                              : tl("buttons.assignTrailer", "Assign Trailer")
                          }
                        >
                          <Container className="assign-truck-icon trailer-icon" size={16} />
                          <span className="assign-truck-label">
                            {location.trailerPlate
                              ? location.trailerPlate
                              : location.trailerId
                              ? (trailersById[String(location.trailerId)] || `#${location.trailerId}`)
                              : tl("buttons.assignTrailer", "Assign Trailer")}
                          </span>
                        </button>

                        {(location.trailerId || location.trailerPlate) && (
                          <button
                            className="remove-truck-btn"
                            title={
                              isAssignmentBlockedNoAlert(location)
                                ? getBlockedAssignmentHoverMessage(location)
                                : tl("tooltips.removeTrailer", "Remove trailer")
                            }
                            disabled={isAssignmentBlockedNoAlert(location)}
                            onClick={() => {
                              if (guardAssignmentAction(location)) return;
                              handleRemoveTrailer(location.id, location.type); // 👈 passa type p/ sabermos PICKUP/DELIVERY
                            }}
                            tabIndex={-1}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>

                       {/* Drivers for PICKUP/DELIVERY */}
                      <div className="assign-truck-btn-wrapper">
                        <button
                          className={`assign-truck-btn ${hasAssignedDriver(location) ? "assigned-driver-btn" : ""}`}
                          disabled={getAssignmentButtonDisabled(location)}
                          onClick={() => {
                            if (guardAssignmentAction(location)) return;
                            openDriverAssignUI(location, location.type.toLowerCase(), 1); // pickup | delivery
                          }}
                          style={{
                            cursor: isAssignmentBlockedNoAlert(location) ? "not-allowed" : "pointer",
                          }}
                          title={
                            isAssignmentBlockedNoAlert(location)
                              ? getBlockedAssignmentHoverMessage(location)
                              : tl("buttons.assignDriver1", "Assign Driver 1")
                          }
                        >
                          <User className="assign-driver-icon" size={16} />
                          <span className="assign-driver-label">
                            {location.driverName
                              ? location.driverName
                              : location.driverId
                              ? tl("labels.driverAssigned", "Driver assigned")
                              : tl("buttons.assignDriver1", "Driver 1")}
                          </span>
                        </button>

                        {(location.driverId || location.driverName) && (
                          <button
                            className="remove-truck-btn"
                            title={
                              isAssignmentBlockedNoAlert(location)
                                ? getBlockedAssignmentHoverMessage(location)
                                : tl("tooltips.removeDriver1", "Remove driver 1")
                            }
                            disabled={isAssignmentBlockedNoAlert(location)}
                            onClick={() => {
                              if (guardAssignmentAction(location)) return;
                              handleRemoveDriver(location.id, location.type.toLowerCase(), 1);
                            }}
                            tabIndex={-1}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>

                      <div className="assign-truck-btn-wrapper">
                        <button
                          className={`assign-truck-btn ${location.driver2Id || location.driver2Name ? "assigned-driver-btn" : ""}`}
                          disabled={getAssignmentButtonDisabled(location)}
                          onClick={() => {
                            if (guardAssignmentAction(location)) return;
                            openDriverAssignUI(location, location.type.toLowerCase(), 2); // pickup | delivery
                          }}
                          style={{
                            cursor: isAssignmentBlockedNoAlert(location) ? "not-allowed" : "pointer",
                          }}
                          title={
                            isAssignmentBlockedNoAlert(location)
                              ? getBlockedAssignmentHoverMessage(location)
                              : tl("buttons.assignDriver2", "Assign Driver 2")
                          }
                        >
                          <User className="assign-driver-icon" size={16} />
                          <span className="assign-driver-label">
                            {location.driver2Name
                              ? location.driver2Name
                              : location.driver2Id
                              ? tl("labels.driverAssigned", "Driver assigned")
                              : tl("buttons.assignDriver2", "Driver 2")}
                          </span>
                        </button>

                        {(location.driver2Id || location.driver2Name) && (
                          <button
                            className="remove-truck-btn"
                            title={
                              isAssignmentBlockedNoAlert(location)
                                ? getBlockedAssignmentHoverMessage(location)
                                : tl("tooltips.removeDriver2", "Remove driver 2")
                            }
                            disabled={isAssignmentBlockedNoAlert(location)}
                            onClick={() => {
                              if (guardAssignmentAction(location)) return;
                              handleRemoveDriver(location.id, location.type.toLowerCase(), 2);
                            }}
                            tabIndex={-1}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* STATUS + PHOTOS */}
                <div className="assign-truck-btn-wrapper status-actions-wrapper">
                  {(() => {
                    const statusLocked =
                      (location.isStatusLocked ?? location.isLocked) || location.isFrozen;

                    const blockedStatusHoverMessage = getBlockedStatusHoverMessage();

                    return (
                      <>
                        {(location.type === "PICKUP" || location.type === "DELIVERY") && (
                          <div className="point-media-actions-row">
                            <button
                              className={`assign-truck-btn photo-btn cmr-photo-btn ${
                                Boolean(location.hasPhotos) ? "assigned-photo-btn" : ""
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPdForPhotos(location.id);
                                setCmrLoadModalOpen(true);
                              }}
                              title={tl("tooltips.photos", "Upload / View photos")}
                            >
                              <FaCamera style={{ marginRight: 8 }} />
                              {tl("buttons.photos", "Photos")}
                            </button>

                            {location.driverCompletionHasIssue === true &&
                              String(location.driverCompletionNote || "").trim() && (
                                <div
                                  className="driver-note-indicator-wrapper"
                                  tabIndex={0}
                                >
                                  <span
                                    className="driver-note-indicator"
                                    aria-label={tl(
                                      "driverOccurrence.noteTitle",
                                      "Nota do motorista"
                                    )}
                                    title={tl(
                                      "driverOccurrence.noteTitle",
                                      "Nota do motorista"
                                    )}
                                  >
                                    <FaCommentDots aria-hidden="true" />
                                  </span>

                                  <div className="driver-note-popover">
                                    <div className="driver-note-popover-header">
                                      <FaCommentDots
                                        className="driver-note-popover-header-icon"
                                        aria-hidden="true"
                                      />

                                      <span className="driver-note-popover-title">
                                        {tl(
                                          "driverOccurrence.noteTitle",
                                          "Nota do motorista"
                                        )}
                                      </span>
                                    </div>

                                    <p className="driver-note-popover-text">
                                      {String(
                                        location.driverCompletionNote || ""
                                      ).trim()}
                                    </p>

                                    {(location.driverCompletionNoteDriverName ||
                                      location.driverCompletionNoteAt) && (
                                      <div className="driver-note-popover-meta">
                                        {location.driverCompletionNoteDriverName && (
                                          <span>
                                            {location.driverCompletionNoteDriverName}
                                          </span>
                                        )}

                                        {location.driverCompletionNoteDriverName &&
                                          location.driverCompletionNoteAt && (
                                            <span
                                              className="driver-note-popover-meta-separator"
                                              aria-hidden="true"
                                            >
                                              ·
                                            </span>
                                          )}

                                        {location.driverCompletionNoteAt && (
                                          <span>
                                            {String(
                                              formatStatusEventDateTime({
                                                eventTimestamp:
                                                  location.driverCompletionNoteAt,
                                              }) || ""
                                            ).trim()}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        )}

                        <div
                          className={`status-tooltip-wrapper ${
                            statusLocked ? "show-status-tooltip" : ""
                          }`}
                          data-tooltip={blockedStatusHoverMessage}
                        >
                          <button
                            className={`assign-truck-btn
                              ${String(location.status || "").toUpperCase() === "COMPLETED" ? "status-completed" : ""}
                              ${String(location.status || "").toUpperCase() === "CANCELLED" ? "status-cancelled" : ""}
                              ${statusLocked ? "status-disabled" : ""}
                            `}
                            onClick={() => {
                              if (statusLocked) return;

                              const tUpper = String(location?.type || "").toUpperCase();
                              const currentUpper = String(location?.status || "").toUpperCase();

                              if (tUpper !== "STRATEGICPOINT" && currentUpper === "PENDING") {
                                const ok = canChangeStatusOffPending(location);

                                if (!ok) {
                                  alert(
                                    tl(
                                      "alerts.statusRequiresTruckDriver",
                                      "❌ You must assign BOTH a truck and a driver before changing the status."
                                    )
                                  );
                                  return;
                                }
                              }

                              setSelectedPDForStatus(location);
                              setStatusModalOpen(true);
                            }}
                            title={
                              statusLocked
                                ? blockedStatusHoverMessage
                                : tl("tooltips.statusClickToUpdate", "Click to update status")
                            }
                          >
                            {renderStatusButtonContent(location)}
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Strategic actions */}
                {location.type === "STRATEGICPOINT" && (
                  <div className="assign-truck-btn-wrapper">
                    <div className="strategic-actions">
                      <button
                        className="edit-strategic-btn"
                        title={
                          isClosedStrategicPoint(location)
                            ? getClosedStrategicPointEditMessage(location)
                            : tl("tooltips.editStrategicPoint", "Edit strategic point")
                        }
                        disabled={location.isFrozen}
                        onClick={async () => {
                          if (guardStrategicPointEditAction(location)) return;

                          try {
                            setIsLoading(true);

                            const { data } = await axios.get(`/pickupdeliveries/by-id/${location.id}`);

                            const raw = data?.pickupDelivery || data?.pd || data?.row || data;

                            const normDate = (d) => {
                              if (!d) return "";
                              const s = String(d);
                              return s.includes("T") ? s.split("T")[0] : s; // YYYY-MM-DD
                            };
                            const normHour = (h) => {
                              if (!h) return "";
                              const s = String(h);
                              return s.length >= 5 ? s.slice(0, 5) : s; // HH:MM
                            };

                            const normalized = {
                              ...raw,
                              location:
                                raw?.location ??
                                raw?.place?.placeName ??
                                raw?.place?.location ??
                                raw?.placeName ??
                                location?.location ??
                                "",

                              date: normDate(raw?.date ?? location?.date),
                              hour: normHour(raw?.hour ?? location?.hour),
                              
                              strategicPointPurpose:
                                String(
                                  raw?.strategicPointPurpose ??
                                  location?.strategicPointPurpose ??
                                  "GENERAL"
                                ).toUpperCase() === "FUEL"
                                  ? "FUEL"
                                  : "GENERAL",

                              hasTrailerSwap:
                                raw?.hasTrailerSwap === true ||
                                String(raw?.hasTrailerSwap || "").toLowerCase() === "true" ||
                                location?.hasTrailerSwap === true ||
                                String(location?.hasTrailerSwap || "").toLowerCase() === "true",

                              address: raw?.address ?? raw?.place?.address ?? "",
                              postalCode: raw?.postalCode ?? raw?.place?.postalCode ?? "",
                              city: raw?.city ?? raw?.place?.city ?? "",
                              country: raw?.country ?? raw?.place?.country ?? "",
                              gMapsLink: raw?.gMapsLink ?? raw?.place?.gMapsLink ?? "",
                              coordinates: raw?.coordinates ?? raw?.place?.coordinates ?? "",
                              contactPhone: raw?.contactPhone ?? raw?.place?.contactPhone ?? "",

                              placeId:
                                raw?.placeId != null ? Number(raw.placeId) :
                                raw?.place?.id != null ? Number(raw.place.id) :
                                null,
                            };

                            setSelectedStrategicPointToEdit(normalized);
                            setEditStrategicModalOpen(true);
                          } catch (e) {
                            console.error("Failed to fetch strategic point full data:", e);
                            alert(tl("alerts.failedFetchStrategicPoint", "Failed to load strategic point details."));
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                      >
                        <FaEdit /> {tl("buttons.edit", "Edit")}
                      </button>

                      <button
                        className="remove-strategic-btn"
                        title={
                          isClosedStrategicPoint(location)
                            ? getClosedStrategicPointEditMessage(location)
                            : tl("tooltips.deleteStrategicPoint", "Delete strategic point")
                        }
                        disabled={location.isFrozen}
                        onClick={() => {
                          if (guardStrategicPointEditAction(location)) return;
                          handleDeleteStrategicPoint(location.id);
                        }}
                      >
                        <FaTrash /> {tl("buttons.delete", "Delete")}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {index < locationsWithLock.length - 1 && <div className="dotted-line" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <ModalPortal>
          {/* Status modal */}
          <StatusSelectorModal
            isOpen={statusModalOpen}
            onClose={() => setStatusModalOpen(false)}
            currentStatus={selectedPDForStatus?.status}
          type={selectedPDForStatus?.type}
          locationName={selectedPDForStatus?.place?.location || selectedPDForStatus?.location}
          onSelect={async (newStatus) => {
            try {
              const upperNew = String(newStatus || "").toUpperCase();
              const tUpper = String(selectedPDForStatus?.type || "").toUpperCase();

              // ✅ Block: PICKUP/DELIVERY cannot leave PENDING without truck+driver
              if (tUpper !== "STRATEGICPOINT" && !isPendingStatus(upperNew)) {
                const ok = canChangeStatusOffPending(selectedPDForStatus);

                if (!ok) {
                  alert(
                    tl(
                      "alerts.statusRequiresTruckDriver",
                      "❌ You must assign BOTH a truck and a driver before changing the status."
                    )
                  );
                  return;
                }
              }

              const { data: updatedStatusPoint } = await axios.put(
                `/pickupdeliveries/${selectedPDForStatus.id}/status`,
                {
                  status: newStatus,
                }
              );

              const nextPoints = (locationsRef.current || []).map((loc) =>
                Number(loc.id) === Number(selectedPDForStatus.id)
                  ? {
                      ...loc,
                      status: newStatus,
                      latestStatusEvent:
                        updatedStatusPoint?.latestStatusEvent ??
                        loc.latestStatusEvent ??
                        null,
                    }
                  : loc
              );

              setLocations(nextPoints);
              setResetKey((prev) => prev + 1);
              onPointsChanged?.(nextPoints);

              // ✅ detect if this is the LAST DELIVERY of that load in the session timeline
              const upper = String(newStatus || "").toUpperCase();
              const isClosing = upper === "COMPLETED" || upper === "CANCELLED";

              const isDelivery = String(selectedPDForStatus?.type || "").toUpperCase() === "DELIVERY";
              const loadId = Number(selectedPDForStatus?.loadId);

              const sorted = Array.isArray(nextPoints) ? [...nextPoints] : [];
              sorted.sort(cmpLocations);

              const deliveriesSameLoad = sorted.filter(
                (p) => String(p?.type || "").toUpperCase() === "DELIVERY" && Number(p?.loadId) === loadId
              );

              const lastDelivery = deliveriesSameLoad.length
                ? deliveriesSameLoad[deliveriesSameLoad.length - 1]
                : null;

              const isLastDelivery =
                !!lastDelivery && Number(lastDelivery.id) === Number(selectedPDForStatus.id);

              setStatusModalOpen(false);

              // ✅ Last delivery closed -> CMR first, inventory only after CMR modal closes
              if (isClosing && isDelivery && isLastDelivery) {
                const updatedSelected =
                  deliveriesSameLoad.find((d) => Number(d.id) === Number(selectedPDForStatus.id)) ||
                  { ...selectedPDForStatus, status: newStatus };

                await startLastDeliveryCmrFlow(updatedSelected, loadId);
              }

              setSelectedPDForStatus(null);
              fetchLocations();
              if (refreshLoads) refreshLoads();
            } catch (err) {
              alert(tl("alerts.updateStatusFailed", "Failed to update status."));
            }
          }}
        />

        <TruckInventoryModal
          isOpen={inventoryModalOpen}
          truck={inventoryTruck}
          initialInventory={inventoryData}
          isSaving={inventorySaving}
          onClose={() => {
            setInventoryModalOpen(false);
          }}
          onContinue={async () => {
            setInventoryModalOpen(false);

            // ✅ se também existe trailer inventory preparado, mostra-o a seguir
            if (trailerMeta?.id && trailerInvData) {
              setTrailerInventoryModalOpen(true);
              return;
            }

            await finalizeAfterInventory(selectedLoadForCmr);
          }}
          onSaveAndContinue={async (vals) => {
            if (!inventoryTruck?.id) {
              setInventoryModalOpen(false);

              // ✅ chain: truck não existe -> tenta trailer -> senão finaliza
              if (trailerMeta?.id && trailerInvData) {
                setTrailerInventoryModalOpen(true);
                return;
              }

              await finalizeAfterInventory(selectedLoadForCmr);
              return;
            }

            try {
              setInventorySaving(true);
              await axios.put(`/trucks/${inventoryTruck.id}`, {
                number_of_straps: vals.number_of_straps ?? 0,
                number_of_ratchets: vals.number_of_ratchets ?? 0,
                number_of_edge_protectors: vals.number_of_edge_protectors ?? 0,
                number_of_anti_slip_mats: vals.number_of_anti_slip_mats ?? 0,
              });
            } catch (e) {
              console.error("Failed to save truck inventory:", e);
              alert(tl("alerts.saveInventoryFailedContinue", "Failed to save inventory. Continuing."));
            } finally {
              setInventorySaving(false);
              setInventoryModalOpen(false);

              // ✅ se também existe trailer inventory preparado, mostra-o a seguir
              if (trailerMeta?.id && trailerInvData) {
                setTrailerInventoryModalOpen(true);
                return;
              }

              await finalizeAfterInventory(selectedLoadForCmr);
            }
          }}
        />

        <TrailerInventoryModal
          isOpen={trailerInventoryModalOpen}
          trailer={trailerMeta}
          initialInventory={trailerInvData}
          isSaving={trailerInvSaving}
          onClose={() => {
            setTrailerInventoryModalOpen(false);
          }}
          onContinue={async () => {
            setTrailerInventoryModalOpen(false);
            await finalizeAfterInventory(selectedLoadForCmr);
          }}
          onSaveAndContinue={async (vals) => {
            if (!trailerMeta?.id) {
              setTrailerInventoryModalOpen(false);
              await finalizeAfterInventory(selectedLoadForCmr);
              return;
            }

            try {
              await axios.put(`/trailers/${trailerMeta.id}/inventory`, {
                numberOfStraps: vals.numberOfStraps ?? 0,
                numberOfRatchets: vals.numberOfRatchets ?? 0,
                numberOfEdgeProtectors: vals.numberOfEdgeProtectors ?? 0,
                numberOfAntiSlipMats: vals.numberOfAntiSlipMats ?? 0,
                numberOfWoodenBeams: vals.numberOfWoodenBeams ?? 0,
                numberOfSidebars: vals.numberOfSidebars ?? 0,
                numberOfFireExtinguishers: vals.numberOfFireExtinguishers ?? 0,
              });
            } catch (e) {
              console.error("Failed to save trailer inventory:", e);
              alert(
                tl(
                  "alerts.saveTrailerInventoryFailedContinue",
                  "Failed to save trailer inventory. Continuing."
                )
              );
            } finally {
              setTrailerInvSaving(false);
              setTrailerInventoryModalOpen(false);
              await finalizeAfterInventory(selectedLoadForCmr);
            }
          }}
        />

          <CMRLoadModal
            isOpen={cmrLoadModalOpen}
            pickupDeliveryId={selectedPdForPhotos}
            onClose={async () => {
              if (pendingInventoryAfterCmr) {
                await continuePendingInventoryAfterCmr();
                return;
              }

              const pdIdJustClosed = selectedPdForPhotos;

              setCmrLoadModalOpen(false);
              setSelectedPdForPhotos(null);

              // ✅ refresh imediato para o botão "Photos" ficar logo pintado
              try {
                if (pdIdJustClosed) {
                  setLocations((prev) =>
                    prev.map((loc) =>
                      Number(loc.id) === Number(pdIdJustClosed)
                        ? { ...loc, hasPhotos: true }
                        : loc
                    )
                  );
                  setResetKey((k) => k + 1);
                }

                await fetchLocations();
                if (refreshLoads) refreshLoads();
              } catch (e) {
                console.error("❌ Failed to refresh photos state after closing CMR modal:", e);
              }
            }}
          />
        </ModalPortal>
      </>
    );
  }
);

export default TransportSessionLocations;
